/**
 * Framework Workspace — Single-framework deep-dive page
 *
 * The most important detail page in the Trust module. Shown when an operator
 * clicks a framework row on /trust/frameworks. Surfaces everything about a
 * single compliance framework (ITAR, CMMC L2, HIPAA, GDPR, SOC 2, ISO 27001,
 * ISO 42001, FINOS AI v2):
 *
 *   - Header with framework metadata, status, setup progress, action buttons
 *   - 5 stat cards: controls health, evidence freshness, findings, risks, assessment
 *   - 6 tabs:
 *       1. Controls          — every control mapped to this framework, sortable
 *       2. Evidence          — every evidence artifact mapped, sortable
 *       3. Setup Wizard      — multi-step framework-specific setup questions
 *       4. Reports           — generated audit reports / export (PDF/Excel/OSCAL)
 *       5. Roadmap / POA&M   — Plan of Action & Milestones (federal) or improvement plan
 *       6. Risks & Policies  — mitigating risks + governing policies
 *
 *   - "Generate Audit Report" dialog (type + date range + sections + watermark)
 *
 * Styled to match the rest of the Trust module exactly.
 */

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState, useEffect } from 'react';
import {
  ArrowLeft, ArrowRight, ShieldCheck, Download, FileText, Settings, Calendar,
  AlertTriangle, CheckCircle2, Clock, FolderArchive, FileWarning,
  RefreshCw, ChevronRight, Sparkles, User, Zap, BookOpen, Brain,
  Target, Flag, Plus, Check, Building2, Layers, ExternalLink,
  TrendingUp, FilePlus, FileCheck, X, Copy,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { SortableHeader, useTableSort } from '@/components/ui/data-table';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  FRAMEWORK_STATUS_STYLES, CONTROL_STATUS_STYLES, RISK_RATING_STYLES,
  FRAMEWORK_CATEGORY_STYLES,
  type Framework, type FrameworkId, type Control, type Evidence,
} from '@/lib/mock-trust-data';
import { useFrameworkControls, useReassignControlOwner, useSetControlStatus } from '@/lib/use-framework-controls';
import { useTrustFrameworks } from '@/lib/use-trust-frameworks';
import { useTrustEvidence } from '@/lib/use-trust-evidence';
import {
  useTrustPoam, useCreatePoamItem, useUpdatePoamItem, useDeletePoamItem,
  type PoamWritePayload,
} from '@/lib/use-trust-poam';
import { PoamEditorDialog } from '@/components/trust/poam-editor-dialog';
import { ControlDetailSheet, ReassignControlDialog } from '@/components/trust/control-detail-sheet';

export const Route = createFileRoute('/trust/frameworks/$frameworkId')({
  component: FrameworkWorkspacePage,
});

// ── Types ──────────────────────────────────────────────────────────────────

type WorkspaceTab = 'controls' | 'evidence' | 'setup' | 'reports' | 'roadmap';

type ReportFormat = 'pdf' | 'excel' | 'oscal';
type ReportFormatLabel = 'PDF' | 'Excel' | 'OSCAL';

interface AuditReport {
  id: string;
  name: string;
  format: ReportFormat;
  generatedDate: string;
  generatedBy: string;
  periodStart: string;
  periodEnd: string;
  totalControls: number;
  passRate: number;
  fileSizeBytes: number;
  watermark: string | null;
}

interface PoamItem {
  id: string;
  name: string;
  description: string;
  status: 'open' | 'in_progress' | 'completed';
  owner: string;
  openDate: string;
  targetCloseDate: string;
  milestones: string[];
}

interface WizardQuestion {
  id: string;
  label: string;
  helpText: string;
  kind: 'text' | 'textarea' | 'select' | 'date' | 'checkbox';
  options?: string[];
  defaultValue?: string | boolean;
  required?: boolean;
}

interface WizardStep {
  id: string;
  title: string;
  description: string;
  questions: WizardQuestion[];
}

// ── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / 86400000);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function progressColor(pct: number): string {
  if (pct >= 90) return 'bg-emerald-500';
  if (pct >= 60) return 'bg-amber-500';
  return 'bg-red-500';
}

function progressTextColor(pct: number): string {
  if (pct >= 90) return 'text-emerald-600 dark:text-emerald-400';
  if (pct >= 60) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

function familyLabel(family: string): string {
  return family.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const AUTOMATION_STYLES: Record<'automated' | 'manual' | 'hybrid', { label: string; color: string; icon: typeof Zap }> = {
  automated: { label: 'Auto', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20', icon: Zap },
  hybrid: { label: 'Hybrid', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20', icon: RefreshCw },
  manual: { label: 'Manual', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20', icon: User },
};

const PRIORITY_STYLES: Record<'critical' | 'high' | 'medium' | 'low', { label: string; color: string }> = {
  critical: { label: 'Critical', color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20' },
  high: { label: 'High', color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20' },
  medium: { label: 'Medium', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' },
  low: { label: 'Low', color: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20' },
};

// ── Per-Framework Setup Wizard Questions ───────────────────────────────────
// Drawn from compliance research — every framework has unique onboarding inputs

const WIZARD_STEPS: Record<FrameworkId, WizardStep[]> = {
  itar: [
    {
      id: 'itar-registration',
      title: 'DDTC Registration',
      description: 'All manufacturers, exporters, and brokers of USML items must register with DDTC.',
      questions: [
        { id: 'itar-registration-number', label: 'DDTC Registration Code (M-code)', helpText: 'Format: M-NNNN-NNN', kind: 'text', required: true, defaultValue: 'M-1482-247' },
        { id: 'itar-registration-expiry', label: 'Registration Expiration Date', helpText: 'Registration must be renewed annually', kind: 'date', required: true, defaultValue: '2027-02-14' },
        { id: 'itar-usml-categories', label: 'USML Categories Handled', helpText: 'Select all USML categories your org handles', kind: 'select', required: true, options: ['Category I — Firearms', 'Category II — Guns and Armament', 'Category IV — Launch Vehicles, Missiles', 'Category VIII — Aircraft', 'Category XI — Military Electronics', 'Category XV — Spacecraft'], defaultValue: 'Category XI — Military Electronics' },
      ],
    },
    {
      id: 'itar-empowered-official',
      title: 'Empowered Official',
      description: 'DDTC requires a designated Empowered Official with authority to bind the organization.',
      questions: [
        { id: 'itar-eo-name', label: 'Empowered Official Full Name', helpText: 'As listed on DS-2032', kind: 'text', required: true, defaultValue: '' },
        { id: 'itar-eo-title', label: 'Title / Role', helpText: '', kind: 'text', required: true, defaultValue: 'VP Compliance' },
        { id: 'itar-eo-email', label: 'Empowered Official Email', helpText: 'DDTC contact address', kind: 'text', required: true, defaultValue: '' },
        { id: 'itar-eo-us-person', label: 'Verified as US Person', helpText: 'Required: US citizen, permanent resident, or protected individual', kind: 'checkbox', defaultValue: true },
      ],
    },
    {
      id: 'itar-us-person-process',
      title: 'US-Person Verification Process',
      description: 'Documentary evidence required — self-declaration is NOT sufficient under ITAR 120.62.',
      questions: [
        { id: 'itar-us-person-method', label: 'Verification Method', helpText: 'How will you verify US Person status?', kind: 'select', required: true, options: ['Documentary evidence review (passport, birth certificate, green card)', 'Third-party verification service', 'I-9 form + supporting documents'], defaultValue: 'Documentary evidence review (passport, birth certificate, green card)' },
        { id: 'itar-reverification-cadence', label: 'Re-verification Cadence', helpText: 'Minimum: annual', kind: 'select', required: true, options: ['annual', 'biannual', 'quarterly', 'continuous'], defaultValue: 'annual' },
        { id: 'itar-retention', label: 'Document Retention Years', helpText: 'Minimum 5 years per ITAR 122.5', kind: 'text', required: true, defaultValue: '5' },
      ],
    },
    {
      id: 'itar-technical-data',
      title: 'Technical Data Handling',
      description: 'Deemed export prevention — ITAR 120.17',
      questions: [
        { id: 'itar-tech-data-scope', label: 'Describe Technical Data You Handle', helpText: 'Drawings, schematics, software, specifications', kind: 'textarea', required: true, defaultValue: 'Defense electronics firmware, schematics, test reports, flight qualification data' },
        { id: 'itar-storage-location', label: 'Primary Storage Location', helpText: 'Must be US-located and US-controlled', kind: 'text', required: true, defaultValue: 'On-prem, Virginia data center' },
        { id: 'itar-cloud-restrictions', label: 'Cloud Storage Restrictions', helpText: '', kind: 'checkbox', defaultValue: true },
      ],
    },
    {
      id: 'itar-license-tracking',
      title: 'Export License Tracking',
      description: 'DSP-5 licenses, TAAs, MLAs, exemptions.',
      questions: [
        { id: 'itar-active-licenses', label: 'Active License Count', helpText: '', kind: 'text', defaultValue: '3' },
        { id: 'itar-license-manager', label: 'License Manager Name', helpText: '', kind: 'text', defaultValue: '' },
      ],
    },
  ],
  cmmc_l2: [
    {
      id: 'cmmc-scope',
      title: 'Assessment Scope',
      description: 'Define which systems, networks, and facilities handle CUI (Controlled Unclassified Information).',
      questions: [
        { id: 'cmmc-in-scope-systems', label: 'In-Scope Systems', helpText: 'List all systems that process/store/transmit CUI', kind: 'textarea', required: true, defaultValue: 'ArgusAI production cluster, document management, M365 GCC-High, Atlassian Jira Federal' },
        { id: 'cmmc-assessment-type', label: 'Assessment Type', helpText: '', kind: 'select', required: true, options: ['Self-Assessment', 'C3PAO Third-Party Assessment (required for L2)', 'DIBCAC (DCMA) Assessment'], defaultValue: 'C3PAO Third-Party Assessment (required for L2)' },
        { id: 'cmmc-c3pao', label: 'C3PAO Partner', helpText: 'Your certified third-party assessor', kind: 'text', defaultValue: 'Schellman & Co.' },
      ],
    },
    {
      id: 'cmmc-ssp',
      title: 'System Security Plan (SSP)',
      description: 'Required under DFARS 252.204-7012 — describes how each of the 110 NIST SP 800-171 practices is implemented.',
      questions: [
        { id: 'cmmc-ssp-current', label: 'SSP Document Version', helpText: '', kind: 'text', defaultValue: 'v2.3' },
        { id: 'cmmc-ssp-last-update', label: 'Last SSP Update', helpText: 'Must be current within 90 days before assessment', kind: 'date', defaultValue: '2026-02-14' },
        { id: 'cmmc-ssp-owner', label: 'SSP Owner', helpText: 'Usually CISO or equivalent', kind: 'text', required: true, defaultValue: '' },
      ],
    },
    {
      id: 'cmmc-poam',
      title: 'Plan of Action & Milestones (POA&M)',
      description: 'Documents controls that are NOT yet fully implemented with a remediation timeline. 180-day maximum closure.',
      questions: [
        { id: 'cmmc-poam-open', label: 'Open POA&M Items', helpText: 'Must be <= 10% of 110 practices (CMMC 32 CFR)', kind: 'text', defaultValue: '8' },
        { id: 'cmmc-poam-closure-sla', label: 'Closure Target', helpText: 'Federal requirement: 180 days', kind: 'text', required: true, defaultValue: '180 days' },
      ],
    },
    {
      id: 'cmmc-incident',
      title: 'Cyber Incident Reporting',
      description: 'DFARS 252.204-7012 requires 72-hour reporting to DoD via DIBNet.',
      questions: [
        { id: 'cmmc-dibnet-registered', label: 'DIBNet Registered', helpText: 'dibnet.dod.mil reporting portal', kind: 'checkbox', defaultValue: true },
        { id: 'cmmc-incident-playbook', label: 'Incident Response Playbook Owner', helpText: '', kind: 'text', required: true, defaultValue: '' },
      ],
    },
    {
      id: 'cmmc-mfa-policy',
      title: 'Multi-Factor Authentication',
      description: 'CMMC L2 requires MFA for all privileged access AND local + network access to CUI.',
      questions: [
        { id: 'cmmc-mfa-method', label: 'Approved MFA Methods', helpText: '', kind: 'select', options: ['Phishing-resistant (FIDO2, PIV-I)', 'TOTP (Authenticator apps)', 'Hardware token (RSA SecurID)', 'SMS (not recommended)'], defaultValue: 'Phishing-resistant (FIDO2, PIV-I)' },
        { id: 'cmmc-mfa-privileged', label: 'MFA required for privileged access', helpText: '', kind: 'checkbox', defaultValue: true },
      ],
    },
    {
      id: 'cmmc-continuous-monitoring',
      title: 'Continuous Monitoring',
      description: 'CMMC L2 requires continuous monitoring of security controls.',
      questions: [
        { id: 'cmmc-siem', label: 'SIEM / Log Aggregator', helpText: '', kind: 'text', defaultValue: 'Splunk + Argus audit hash-chain' },
        { id: 'cmmc-scan-frequency', label: 'Vulnerability Scan Frequency', helpText: '', kind: 'select', options: ['monthly', 'weekly', 'continuous'], defaultValue: 'weekly' },
      ],
    },
  ],
  hipaa: [
    {
      id: 'hipaa-role',
      title: 'HIPAA Role',
      description: 'Are you a Covered Entity or Business Associate?',
      questions: [
        { id: 'hipaa-entity-type', label: 'Entity Type', helpText: '', kind: 'select', required: true, options: ['Covered Entity (health plan, provider, clearinghouse)', 'Business Associate', 'Subcontractor of Business Associate', 'Hybrid Entity'], defaultValue: 'Business Associate' },
        { id: 'hipaa-privacy-officer', label: 'Privacy Officer', helpText: 'Required by § 164.530(a)', kind: 'text', required: true, defaultValue: 'Elena Petrov' },
        { id: 'hipaa-security-officer', label: 'Security Officer', helpText: 'Required by § 164.308(a)(2)', kind: 'text', required: true, defaultValue: '' },
      ],
    },
    {
      id: 'hipaa-phi-scope',
      title: 'PHI Scope',
      description: 'What PHI do you handle and where is it stored?',
      questions: [
        { id: 'hipaa-phi-categories', label: 'PHI Categories Handled', helpText: '', kind: 'textarea', required: true, defaultValue: 'Patient demographics, clinical notes, medication records, lab results' },
        { id: 'hipaa-phi-record-count', label: 'Approximate PHI Record Count', helpText: 'Affects breach notification thresholds', kind: 'text', defaultValue: '127,000' },
      ],
    },
    {
      id: 'hipaa-2026-update',
      title: '2026 Security Rule Update',
      description: 'Enhanced Security Rule (proposed Jan 2025, effective 2026). Adds mandatory controls.',
      questions: [
        { id: 'hipaa-mfa-mandatory', label: 'MFA mandatory for all workforce', helpText: 'New 2026 requirement', kind: 'checkbox', defaultValue: true },
        { id: 'hipaa-encryption-rest', label: 'Encryption-at-rest enabled', helpText: 'New 2026 mandatory requirement', kind: 'checkbox', defaultValue: true },
        { id: 'hipaa-vuln-scanning', label: 'Vulnerability scanning every 6 months', helpText: 'New 2026 requirement', kind: 'checkbox', defaultValue: true },
        { id: 'hipaa-asset-inventory', label: 'Asset inventory maintained', helpText: 'New 2026 mandatory requirement', kind: 'checkbox', defaultValue: true },
      ],
    },
    {
      id: 'hipaa-baa',
      title: 'Business Associate Agreements',
      description: 'Track BAAs with every vendor handling PHI.',
      questions: [
        { id: 'hipaa-baa-count', label: 'Active BAAs', helpText: '', kind: 'text', defaultValue: '14' },
        { id: 'hipaa-baa-review', label: 'BAA Review Cadence', helpText: '', kind: 'select', options: ['annual', 'biannual', 'on_vendor_change'], defaultValue: 'annual' },
      ],
    },
    {
      id: 'hipaa-breach',
      title: 'Breach Notification Process',
      description: 'HIPAA Breach Notification Rule § 164.404/408/410. HHS notification within 60 days for >=500 records.',
      questions: [
        { id: 'hipaa-breach-lead', label: 'Breach Response Lead', helpText: '', kind: 'text', required: true, defaultValue: 'Elena Petrov' },
        { id: 'hipaa-ocr-portal', label: 'HHS OCR Portal Registered', helpText: 'ocrportal.hhs.gov', kind: 'checkbox', defaultValue: true },
      ],
    },
  ],
  gdpr: [
    {
      id: 'gdpr-role',
      title: 'GDPR Role',
      description: 'Controller, Processor, or Joint Controller?',
      questions: [
        { id: 'gdpr-role-type', label: 'Role', helpText: '', kind: 'select', required: true, options: ['Data Controller', 'Data Processor', 'Joint Controller'], defaultValue: 'Data Controller' },
        { id: 'gdpr-dpo-name', label: 'Data Protection Officer (DPO)', helpText: 'Mandatory if processing special category data at scale', kind: 'text', required: true, defaultValue: 'Sophie Laurent' },
        { id: 'gdpr-dpo-email', label: 'DPO Email', helpText: 'Must be public contact point', kind: 'text', required: true, defaultValue: 'dpo@argusai.com' },
      ],
    },
    {
      id: 'gdpr-lead-supervisory',
      title: 'Lead Supervisory Authority',
      description: 'One-stop shop mechanism for cross-border processing.',
      questions: [
        { id: 'gdpr-lead-dpa', label: 'Lead Supervisory Authority', helpText: '', kind: 'select', required: true, options: ['CNIL (France)', 'BfDI (Germany — federal)', 'Garante (Italy)', 'AEPD (Spain)', 'DPC (Ireland)', 'Datatilsynet (Denmark)', 'ICO (UK — separate post-Brexit)'], defaultValue: 'CNIL (France)' },
        { id: 'gdpr-eu-representative', label: 'EU Representative (non-EU controllers)', helpText: 'Art. 27 requirement', kind: 'text', defaultValue: 'CompliancePath GmbH, Berlin' },
      ],
    },
    {
      id: 'gdpr-lawful-basis',
      title: 'Lawful Basis',
      description: 'Art. 6 — each processing activity must have a lawful basis.',
      questions: [
        { id: 'gdpr-primary-basis', label: 'Primary Lawful Basis', helpText: '', kind: 'select', required: true, options: ['Consent (Art. 6(1)(a))', 'Contract (Art. 6(1)(b))', 'Legal Obligation (Art. 6(1)(c))', 'Vital Interests (Art. 6(1)(d))', 'Public Task (Art. 6(1)(e))', 'Legitimate Interests (Art. 6(1)(f))'], defaultValue: 'Contract (Art. 6(1)(b))' },
        { id: 'gdpr-special-category', label: 'Special Category Data Processed', helpText: 'Art. 9 data (health, religion, etc.)', kind: 'checkbox', defaultValue: false },
      ],
    },
    {
      id: 'gdpr-cross-border',
      title: 'Cross-Border Transfers',
      description: 'Transfers outside EU/EEA require safeguards (Chapter V).',
      questions: [
        { id: 'gdpr-transfer-mechanism', label: 'Transfer Mechanism', helpText: '', kind: 'select', options: ['Adequacy Decision', 'Standard Contractual Clauses (SCCs)', 'Binding Corporate Rules', 'Derogations (Art. 49)'], defaultValue: 'Standard Contractual Clauses (SCCs)' },
        { id: 'gdpr-tia-completed', label: 'Transfer Impact Assessment (TIA) completed', helpText: 'Required after Schrems II', kind: 'checkbox', defaultValue: true },
      ],
    },
    {
      id: 'gdpr-dsar',
      title: 'Data Subject Rights',
      description: 'Art. 15-22 — access, rectification, erasure, portability, objection.',
      questions: [
        { id: 'gdpr-dsar-sla', label: 'DSAR Response SLA', helpText: 'Max 30 days per Art. 12(3)', kind: 'text', required: true, defaultValue: '30 days' },
        { id: 'gdpr-dsar-verification', label: 'Identity Verification Method', helpText: '', kind: 'text', defaultValue: 'Government ID + account security challenge' },
      ],
    },
    {
      id: 'gdpr-breach',
      title: '72-Hour Breach Notification',
      description: 'Art. 33 — notify supervisory authority within 72 hours of awareness.',
      questions: [
        { id: 'gdpr-breach-lead', label: 'Breach Response Lead', helpText: '', kind: 'text', required: true, defaultValue: 'Sophie Laurent' },
        { id: 'gdpr-breach-72h', label: '72-hour process documented & tested', helpText: '', kind: 'checkbox', defaultValue: true },
      ],
    },
  ],
  soc2: [
    {
      id: 'soc2-scope',
      title: 'Scope & Trust Service Criteria',
      description: 'Select which TSCs are in scope for your Type II audit.',
      questions: [
        { id: 'soc2-audit-type', label: 'Audit Type', helpText: '', kind: 'select', required: true, options: ['SOC 2 Type I (point-in-time)', 'SOC 2 Type II (6-12 month operating period)'], defaultValue: 'SOC 2 Type II (6-12 month operating period)' },
        { id: 'soc2-tsc-security', label: 'Security (required)', helpText: 'Always required', kind: 'checkbox', defaultValue: true },
        { id: 'soc2-tsc-availability', label: 'Availability', helpText: '', kind: 'checkbox', defaultValue: true },
        { id: 'soc2-tsc-processing', label: 'Processing Integrity', helpText: '', kind: 'checkbox', defaultValue: false },
        { id: 'soc2-tsc-confidentiality', label: 'Confidentiality', helpText: '', kind: 'checkbox', defaultValue: true },
        { id: 'soc2-tsc-privacy', label: 'Privacy', helpText: '', kind: 'checkbox', defaultValue: false },
      ],
    },
    {
      id: 'soc2-cpa',
      title: 'CPA Firm',
      description: 'SOC 2 audits must be performed by a licensed AICPA firm.',
      questions: [
        { id: 'soc2-cpa-firm', label: 'CPA Firm', helpText: '', kind: 'text', required: true, defaultValue: 'Schellman & Co.' },
        { id: 'soc2-audit-window-start', label: 'Audit Window Start', helpText: '', kind: 'date', required: true, defaultValue: '2025-10-01' },
        { id: 'soc2-audit-window-end', label: 'Audit Window End', helpText: '', kind: 'date', required: true, defaultValue: '2026-09-30' },
      ],
    },
    {
      id: 'soc2-carve-outs',
      title: 'Sub-service Organizations',
      description: 'Identify sub-service orgs (cloud providers) and their inclusion method.',
      questions: [
        { id: 'soc2-subservice-method', label: 'Sub-service Method', helpText: '', kind: 'select', options: ['Inclusive (auditor tests sub-service controls)', 'Carve-out (relied on via SOC report)'], defaultValue: 'Carve-out (relied on via SOC report)' },
        { id: 'soc2-subservice-list', label: 'Sub-service Organizations', helpText: '', kind: 'textarea', defaultValue: 'AWS (SOC 2 Type II reviewed), Datadog, PagerDuty' },
      ],
    },
    {
      id: 'soc2-control-environment',
      title: 'Control Environment',
      description: 'Management philosophy, governance, code of conduct.',
      questions: [
        { id: 'soc2-board-oversight', label: 'Board of Directors oversight cadence', helpText: '', kind: 'select', options: ['quarterly', 'biannual', 'annual'], defaultValue: 'quarterly' },
        { id: 'soc2-code-of-conduct', label: 'Code of Conduct published', helpText: '', kind: 'checkbox', defaultValue: true },
        { id: 'soc2-risk-assessment-freq', label: 'Risk assessment frequency', helpText: '', kind: 'select', options: ['annual', 'biannual', 'quarterly', 'continuous'], defaultValue: 'annual' },
      ],
    },
  ],
  iso27001: [
    {
      id: 'iso-scope',
      title: 'ISMS Scope',
      description: 'Define the boundaries of the Information Security Management System (ISO 27001 Clause 4.3).',
      questions: [
        { id: 'iso-isms-scope-statement', label: 'ISMS Scope Statement', helpText: 'Will appear on certificate', kind: 'textarea', required: true, defaultValue: 'The Information Security Management System covers the design, development, deployment, and operation of the ArgusAI enterprise AI platform.' },
        { id: 'iso-excluded-assets', label: 'Excluded Assets / Facilities', helpText: '', kind: 'textarea', defaultValue: 'Marketing website (hosted externally), customer-operated air-gap deployments (customer ISMS)' },
      ],
    },
    {
      id: 'iso-soa',
      title: 'Statement of Applicability (SoA)',
      description: '93 Annex A controls — justify each as Applied, Not Applied, or Not Applicable.',
      questions: [
        { id: 'iso-soa-version', label: 'SoA Version', helpText: '', kind: 'text', defaultValue: 'v4.2' },
        { id: 'iso-soa-approved-by', label: 'SoA Approved By', helpText: '', kind: 'text', required: true, defaultValue: '' },
        { id: 'iso-soa-last-review', label: 'Last Review Date', helpText: '', kind: 'date', defaultValue: '2026-01-15' },
      ],
    },
    {
      id: 'iso-risk-treatment',
      title: 'Risk Treatment Plan',
      description: 'Document the treatment (mitigate/avoid/transfer/accept) for every identified risk.',
      questions: [
        { id: 'iso-risk-methodology', label: 'Risk Methodology', helpText: '', kind: 'select', options: ['ISO 27005', 'NIST SP 800-30', 'FAIR', 'OCTAVE'], defaultValue: 'ISO 27005' },
        { id: 'iso-risk-appetite', label: 'Risk Appetite Statement', helpText: '', kind: 'textarea', required: true, defaultValue: 'Low tolerance for data confidentiality breaches. Medium tolerance for operational availability (target 99.9%).' },
      ],
    },
    {
      id: 'iso-certification',
      title: 'Certification Body',
      description: 'UKAS/ANAB-accredited certifying body.',
      questions: [
        { id: 'iso-certification-body', label: 'Certification Body', helpText: '', kind: 'text', required: true, defaultValue: 'BSI Group' },
        { id: 'iso-stage1-date', label: 'Stage 1 Audit Date', helpText: '', kind: 'date', defaultValue: '2025-10-15' },
        { id: 'iso-stage2-date', label: 'Stage 2 Audit Date', helpText: '', kind: 'date', defaultValue: '2025-12-10' },
      ],
    },
  ],
  iso42001: [
    {
      id: 'iso-42001-scope',
      title: 'AI Management System Scope',
      description: 'Define which AI systems are in scope for the AIMS (ISO 42001 Clause 4.3).',
      questions: [
        { id: 'iso-42001-aims-scope', label: 'AIMS Scope Statement', helpText: '', kind: 'textarea', required: true, defaultValue: 'All AI systems in production — 14 built-in agents, LoRA adapters, custom agents — including RAG retrieval, generation, verification pipelines.' },
        { id: 'iso-42001-ai-lead', label: 'AI Management System Lead', helpText: '', kind: 'text', required: true, defaultValue: '' },
      ],
    },
    {
      id: 'iso-42001-inventory',
      title: 'AI System Inventory (A.6.2)',
      description: 'Every AI system documented with intended use, data inputs, outputs, limitations.',
      questions: [
        { id: 'iso-42001-systems-count', label: 'AI Systems Inventoried', helpText: '', kind: 'text', defaultValue: '14' },
        { id: 'iso-42001-model-cards', label: 'Model Cards published for all production systems', helpText: '', kind: 'checkbox', defaultValue: false },
      ],
    },
    {
      id: 'iso-42001-lifecycle',
      title: 'AI Lifecycle Management (A.6.2.5)',
      description: 'Design, development, deployment, monitoring, retirement procedures.',
      questions: [
        { id: 'iso-42001-design-review', label: 'Design review required before production', helpText: '', kind: 'checkbox', defaultValue: true },
        { id: 'iso-42001-retirement-proc', label: 'Retirement procedure documented', helpText: '', kind: 'checkbox', defaultValue: true },
      ],
    },
    {
      id: 'iso-42001-third-party',
      title: 'Third-Party AI (A.10)',
      description: 'Foundation models, API services, embedding providers.',
      questions: [
        { id: 'iso-42001-third-party-ai', label: 'List Third-Party AI Components', helpText: '', kind: 'textarea', defaultValue: 'Qwen 2.5 72B (on-prem, Alibaba license), DeBERTa NLI, nomic-embed-text' },
      ],
    },
    {
      id: 'iso-42001-data-governance',
      title: 'Data Governance (A.7)',
      description: 'Training data lineage, bias testing, quality controls.',
      questions: [
        { id: 'iso-42001-bias-testing', label: 'Bias testing cadence', helpText: '', kind: 'select', options: ['monthly', 'quarterly', 'per-deployment', 'continuous'], defaultValue: 'quarterly' },
        { id: 'iso-42001-data-lineage', label: 'Data lineage tracked end-to-end', helpText: '', kind: 'checkbox', defaultValue: true },
      ],
    },
  ],
  finos_ai_v2: [
    {
      id: 'finos-inventory',
      title: 'AI System Inventory',
      description: 'FINOS GOV-1 — inventory every LLM, RAG pipeline, and agent.',
      questions: [
        { id: 'finos-inventory-count', label: 'Systems Inventoried', helpText: '', kind: 'text', defaultValue: '14' },
        { id: 'finos-inventory-lead', label: 'Inventory Owner', helpText: '', kind: 'text', required: true, defaultValue: '' },
      ],
    },
    {
      id: 'finos-hallucination',
      title: 'Hallucination Defense (LLM-1)',
      description: 'Faithfulness verifier with metric tracking.',
      questions: [
        { id: 'finos-faithfulness-threshold', label: 'Faithfulness Score Threshold', helpText: 'Target: >= 0.92', kind: 'text', defaultValue: '0.92' },
        { id: 'finos-hallucination-max', label: 'Max Hallucination Rate', helpText: '%', kind: 'text', defaultValue: '2.0' },
      ],
    },
    {
      id: 'finos-injection',
      title: 'Prompt Injection Defense (LLM-2)',
      description: '4-layer defense: input normalization, regex, LLM classifier, topic drift.',
      questions: [
        { id: 'finos-injection-layers', label: 'Defense Layers Active', helpText: '', kind: 'text', defaultValue: '4' },
        { id: 'finos-injection-block-rate', label: 'Target Block Rate', helpText: '%', kind: 'text', defaultValue: '99.5' },
      ],
    },
    {
      id: 'finos-rbac',
      title: 'RBAC on Retrieval (RAG-2)',
      description: 'Classification gate + RLS + permission checks on every retrieval.',
      questions: [
        { id: 'finos-rbac-classification', label: 'Classification gate enabled', helpText: '', kind: 'checkbox', defaultValue: true },
        { id: 'finos-rbac-rls', label: 'RLS enabled on all tenant-scoped tables', helpText: '', kind: 'checkbox', defaultValue: true },
      ],
    },
  ],

  nist_ai_rmf: [
    {
      id: 'nist-govern',
      title: 'Govern',
      description: 'Culture of risk management around AI systems.',
      questions: [
        { id: 'nist-govern-policy', label: 'AI Governance policy in place', helpText: 'Policy defining AI use, acceptable risk, roles', kind: 'checkbox', defaultValue: false },
        { id: 'nist-govern-role', label: 'AI Risk Officer', helpText: 'Designated accountable role', kind: 'text', required: true, defaultValue: '' },
      ],
    },
    {
      id: 'nist-map',
      title: 'Map',
      description: 'Establish context for AI risks.',
      questions: [
        { id: 'nist-map-inventory', label: 'AI Systems inventory complete', helpText: 'All production AI registered in Trust', kind: 'checkbox', defaultValue: false },
        { id: 'nist-map-risk-categories', label: 'Risk categories documented', helpText: '', kind: 'textarea', defaultValue: '' },
      ],
    },
    {
      id: 'nist-measure',
      title: 'Measure',
      description: 'Assess, analyze, and track AI risks.',
      questions: [
        { id: 'nist-measure-metrics', label: 'Performance metrics collected', helpText: 'Faithfulness, hallucination rate, bias scores', kind: 'checkbox', defaultValue: false },
        { id: 'nist-measure-cadence', label: 'Evaluation cadence', helpText: '', kind: 'select', options: ['continuous', 'monthly', 'quarterly'], defaultValue: 'quarterly' },
      ],
    },
    {
      id: 'nist-manage',
      title: 'Manage',
      description: 'Allocate risk resources, respond to issues.',
      questions: [
        { id: 'nist-manage-response-plan', label: 'Incident response plan for AI issues', helpText: '', kind: 'checkbox', defaultValue: false },
      ],
    },
  ],

  fedramp_moderate: [
    {
      id: 'fedramp-authorization',
      title: 'Authorization Path',
      description: 'Determine JAB vs Agency authorization approach.',
      questions: [
        { id: 'fedramp-path', label: 'Authorization path', helpText: '', kind: 'select', required: true, options: ['JAB P-ATO', 'Agency ATO', 'Tailored'], defaultValue: 'Agency ATO' },
        { id: 'fedramp-sponsor', label: 'Sponsoring federal agency', helpText: 'Required for Agency ATO path', kind: 'text', defaultValue: '' },
        { id: 'fedramp-3pao', label: '3PAO selected', helpText: 'FedRAMP-recognized third-party assessor', kind: 'text', required: true, defaultValue: '' },
      ],
    },
    {
      id: 'fedramp-system-boundary',
      title: 'System Boundary',
      description: 'Define FedRAMP authorization boundary.',
      questions: [
        { id: 'fedramp-boundary', label: 'System boundary description', helpText: 'Cloud environment, data flows, interconnections', kind: 'textarea', required: true, defaultValue: '' },
        { id: 'fedramp-iaas', label: 'Underlying IaaS', helpText: 'AWS GovCloud, Azure Government, GCP Assured Workloads', kind: 'text', required: true, defaultValue: '' },
      ],
    },
  ],

  pci_dss: [
    {
      id: 'pci-merchant-level',
      title: 'Merchant / Service Provider Level',
      description: 'PCI DSS obligations depend on transaction volume.',
      questions: [
        { id: 'pci-level', label: 'Level', helpText: '', kind: 'select', required: true, options: ['Level 1 (>6M transactions)', 'Level 2 (1M-6M)', 'Level 3 (20K-1M e-commerce)', 'Level 4 (<20K e-commerce)'], defaultValue: 'Level 4 (<20K e-commerce)' },
        { id: 'pci-qsa', label: 'QSA assessor', helpText: 'Qualified Security Assessor', kind: 'text', defaultValue: '' },
      ],
    },
    {
      id: 'pci-scope',
      title: 'CDE Scope',
      description: 'Cardholder Data Environment scope.',
      questions: [
        { id: 'pci-cde-description', label: 'CDE description', helpText: '', kind: 'textarea', required: true, defaultValue: '' },
        { id: 'pci-tokenization', label: 'Tokenization used', helpText: '', kind: 'checkbox', defaultValue: true },
      ],
    },
  ],
};

// ── Per-Framework Mock Reports ─────────────────────────────────────────────

function generateMockReports(fw: Framework): AuditReport[] {
  const base = fw.shortName;
  const now = Date.now();
  const d = (ago: number) => new Date(now - ago * 86400000).toISOString();

  const common: Omit<AuditReport, 'id' | 'name' | 'format' | 'generatedDate' | 'generatedBy' | 'periodStart' | 'periodEnd' | 'totalControls' | 'passRate' | 'fileSizeBytes' | 'watermark'> = {} as any;
  void common;

  const reports: AuditReport[] = [
    {
      id: `${fw.id}-rpt-q1-2026`,
      name: `${base} — Q1 2026 Controls Report`,
      format: 'pdf',
      generatedDate: d(12),
      generatedBy: 'system',
      periodStart: d(101),
      periodEnd: d(12),
      totalControls: fw.controlCount,
      passRate: Math.round((fw.controlsPassing / Math.max(fw.controlsPassing + fw.controlsFailing, 1)) * 100),
      fileSizeBytes: 4_820_000,
      watermark: 'ArgusAI CONFIDENTIAL',
    },
    {
      id: `${fw.id}-rpt-annual-2025`,
      name: `${base} — Annual Compliance Report (2025)`,
      format: 'pdf',
      generatedDate: d(98),
      generatedBy: fw.assessor ?? 'Internal Compliance',
      periodStart: d(463),
      periodEnd: d(98),
      totalControls: fw.controlCount,
      passRate: 89,
      fileSizeBytes: 12_340_000,
      watermark: 'ArgusAI CONFIDENTIAL',
    },
    {
      id: `${fw.id}-rpt-evidence`,
      name: `${base} — Evidence Export (Excel)`,
      format: 'excel',
      generatedDate: d(22),
      generatedBy: 'system',
      periodStart: d(120),
      periodEnd: d(22),
      totalControls: fw.controlCount,
      passRate: Math.round((fw.controlsPassing / Math.max(fw.controlsPassing + fw.controlsFailing, 1)) * 100),
      fileSizeBytes: 2_410_000,
      watermark: null,
    },
  ];

  // Framework-specific reports
  if (fw.id === 'cmmc_l2') {
    reports.unshift({
      id: 'cmmc-ssp-2026',
      name: 'CMMC L2 System Security Plan — 2026',
      format: 'pdf',
      generatedDate: d(45),
      generatedBy: 'system',
      periodStart: d(365),
      periodEnd: d(45),
      totalControls: fw.controlCount,
      passRate: 79,
      fileSizeBytes: 24_820_000,
      watermark: 'DoD DISTRIBUTION C',
    });
    reports.push({
      id: 'cmmc-oscal-2026',
      name: 'CMMC L2 — OSCAL SSP Export',
      format: 'oscal',
      generatedDate: d(45),
      generatedBy: 'system',
      periodStart: d(365),
      periodEnd: d(45),
      totalControls: fw.controlCount,
      passRate: 79,
      fileSizeBytes: 840_000,
      watermark: null,
    });
  }

  if (fw.id === 'itar') {
    reports.unshift({
      id: 'itar-manual-2026',
      name: 'Annual ITAR Compliance Manual — 2026',
      format: 'pdf',
      generatedDate: d(67),
      generatedBy: 'system',
      periodStart: d(432),
      periodEnd: d(67),
      totalControls: fw.controlCount,
      passRate: 93,
      fileSizeBytes: 8_710_000,
      watermark: 'ITAR CONTROLLED',
    });
  }

  if (fw.id === 'hipaa') {
    reports.unshift({
      id: 'hipaa-risk-analysis-2026',
      name: 'HIPAA Risk Analysis — 2026 Q1',
      format: 'pdf',
      generatedDate: d(30),
      generatedBy: 'system',
      periodStart: d(120),
      periodEnd: d(30),
      totalControls: fw.controlCount,
      passRate: 92,
      fileSizeBytes: 6_120_000,
      watermark: 'PHI — HIPAA RESTRICTED',
    });
  }

  if (fw.id === 'soc2') {
    reports.unshift({
      id: 'soc2-type2-final',
      name: 'SOC 2 Type II Final Report (signed)',
      format: 'pdf',
      generatedDate: d(180),
      generatedBy: 'Schellman & Co.',
      periodStart: d(545),
      periodEnd: d(180),
      totalControls: fw.controlCount,
      passRate: 97,
      fileSizeBytes: 14_850_000,
      watermark: 'AICPA AUDITOR CONFIDENTIAL',
    });
  }

  if (fw.id === 'gdpr') {
    reports.unshift({
      id: 'gdpr-dpia-2026',
      name: 'Data Protection Impact Assessment — 2026',
      format: 'pdf',
      generatedDate: d(52),
      generatedBy: 'Sophie Laurent (DPO)',
      periodStart: d(120),
      periodEnd: d(52),
      totalControls: fw.controlCount,
      passRate: 88,
      fileSizeBytes: 5_620_000,
      watermark: 'GDPR CONFIDENTIAL',
    });
  }

  if (fw.id === 'iso27001') {
    reports.unshift({
      id: 'iso-soa-2026',
      name: 'ISO 27001 Statement of Applicability v4.2',
      format: 'pdf',
      generatedDate: d(95),
      generatedBy: 'system',
      periodStart: d(365),
      periodEnd: d(95),
      totalControls: fw.controlCount,
      passRate: 91,
      fileSizeBytes: 3_420_000,
      watermark: 'ISO ISMS CONFIDENTIAL',
    });
  }

  if (fw.id === 'iso42001') {
    reports.unshift({
      id: 'iso-42001-aims-2026',
      name: 'ISO 42001 AI Management System Manual',
      format: 'pdf',
      generatedDate: d(38),
      generatedBy: 'system',
      periodStart: d(150),
      periodEnd: d(38),
      totalControls: fw.controlCount,
      passRate: 85,
      fileSizeBytes: 7_120_000,
      watermark: 'AI GOVERNANCE',
    });
  }

  if (fw.id === 'finos_ai_v2') {
    reports.unshift({
      id: 'finos-ai-risk-2026',
      name: 'FINOS AI Risk Assessment — 2026 Q1',
      format: 'pdf',
      generatedDate: d(15),
      generatedBy: 'system',
      periodStart: d(105),
      periodEnd: d(15),
      totalControls: fw.controlCount,
      passRate: 94,
      fileSizeBytes: 4_210_000,
      watermark: null,
    });
  }

  return reports;
}

// ── Per-Framework Mock POA&M / Improvement Plan ────────────────────────────

function generatePoam(fw: Framework): PoamItem[] {
  const now = Date.now();
  const d = (off: number) => new Date(now + off * 86400000).toISOString();

  const common: PoamItem[] = [
    {
      id: `${fw.id}-poam-1`,
      name: 'Complete bias testing for 3 remaining production agents',
      description: 'Model cards are missing bias test results for Troubleshooting, Compliance, and Shift Handover agents. Required for ISO 42001 A.6.2.',
      status: 'in_progress',
      owner: 'unassigned',
      openDate: new Date(now - 42 * 86400000).toISOString(),
      targetCloseDate: d(45),
      milestones: ['Define bias test suite (done)', 'Execute tests against golden dataset (in progress)', 'Publish results in model cards'],
    },
    {
      id: `${fw.id}-poam-2`,
      name: 'Quarterly access review for privileged users',
      description: 'Annual access review completed, but quarterly reviews have slipped for Q4 2025.',
      status: 'in_progress',
      owner: 'unassigned',
      openDate: new Date(now - 28 * 86400000).toISOString(),
      targetCloseDate: d(17),
      milestones: ['Export user roster', 'Managers sign off', 'Update audit evidence'],
    },
    {
      id: `${fw.id}-poam-3`,
      name: 'Remediate 1 stale verification evidence (US Person)',
      description: 'User kelly.morrison@argusai.com has expired US-Person verification. Renewal package sent 2026-04-12.',
      status: 'open',
      owner: 'unassigned',
      openDate: new Date(now - 7 * 86400000).toISOString(),
      targetCloseDate: d(14),
      milestones: ['Send renewal package (done)', 'Receive & validate documents', 'Update verification record'],
    },
  ];

  if (fw.id === 'cmmc_l2') {
    return [
      ...common,
      {
        id: 'cmmc-poam-media-sanitize',
        name: 'Media sanitization procedure for CUI disposal',
        description: 'MP.L2-3.8.3 — Sanitize system media containing CUI before disposal. Currently using vendor destruction with certificate; procedure not yet documented.',
        status: 'open',
        owner: 'unassigned',
        openDate: new Date(now - 85 * 86400000).toISOString(),
        targetCloseDate: d(95),
        milestones: ['Draft procedure (due)', 'Review with DoD subcontractor lead', 'Publish & train workforce'],
      },
      {
        id: 'cmmc-poam-insider-threat',
        name: 'Insider threat program formalization',
        description: 'PS.L2-3.9.2 — Ensure personnel procedures include insider threat awareness. Program exists informally; formal policy + training missing.',
        status: 'in_progress',
        owner: 'unassigned',
        openDate: new Date(now - 60 * 86400000).toISOString(),
        targetCloseDate: d(120),
        milestones: ['Policy drafted (done)', 'Approval by C-suite', 'Rollout training to all personnel'],
      },
      {
        id: 'cmmc-poam-scan-cadence',
        name: 'Weekly vulnerability scanning cadence enforcement',
        description: 'RA.L2-3.11.2 — Vulnerability scans running bi-weekly; target is weekly.',
        status: 'in_progress',
        owner: 'unassigned',
        openDate: new Date(now - 14 * 86400000).toISOString(),
        targetCloseDate: d(30),
        milestones: ['Update scanner schedule', 'Confirm 4 consecutive weeks', 'Update evidence'],
      },
    ];
  }

  if (fw.id === 'itar') {
    return [
      ...common,
      {
        id: 'itar-poam-deemed-export',
        name: 'Formalize deemed export training for non-US personnel',
        description: 'Non-US Person access is already physically & logically restricted, but training module for managers on deemed export risk is in draft.',
        status: 'in_progress',
        owner: 'unassigned',
        openDate: new Date(now - 20 * 86400000).toISOString(),
        targetCloseDate: d(60),
        milestones: ['Draft training (done)', 'SME review', 'Deploy to LMS'],
      },
    ];
  }

  if (fw.id === 'hipaa') {
    return [
      ...common,
      {
        id: 'hipaa-poam-mfa-workforce',
        name: '2026 Update — MFA for entire workforce',
        description: 'Currently MFA required for privileged users only. 2026 Security Rule makes MFA mandatory for all workforce with PHI access.',
        status: 'in_progress',
        owner: 'unassigned',
        openDate: new Date(now - 50 * 86400000).toISOString(),
        targetCloseDate: d(40),
        milestones: ['Enroll all users (in progress, 94%)', 'Verify enforcement', 'Update SRA'],
      },
    ];
  }

  return common;
}

// ── Page ───────────────────────────────────────────────────────────────────

function FrameworkWorkspacePage() {
  const { frameworkId } = Route.useParams();
  const navigate = useNavigate();

  const { data: frameworks = [], isLoading } = useTrustFrameworks();

  const framework = useMemo<Framework | undefined>(
    () => frameworks.find(f => f.id === frameworkId as FrameworkId),
    [frameworks, frameworkId],
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <ShieldCheck className="h-14 w-14 text-muted-foreground/20 mb-4 animate-pulse" />
          <p className="text-sm text-muted-foreground">Loading framework...</p>
        </div>
      </div>
    );
  }

  // 404 state
  if (!framework) {
    return (
      <div className="p-6">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <ShieldCheck className="h-14 w-14 text-muted-foreground/20 mb-4" />
          <h1 className="text-xl font-semibold">Framework not found</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-md">
            No framework matched the identifier <code className="px-1.5 py-0.5 rounded bg-muted text-xs">{frameworkId}</code>.
            It may have been removed, not yet adopted, or renamed.
          </p>
          <Button
            size="sm"
            className="mt-6 gap-1.5"
            onClick={() => navigate({ to: '/trust/frameworks' } as any)}
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Frameworks
          </Button>
        </div>
      </div>
    );
  }

  return <FrameworkWorkspace framework={framework} />;
}

// ── Main Workspace ─────────────────────────────────────────────────────────

function FrameworkWorkspace({ framework }: { framework: Framework }) {
  const [activeTab, setActiveTab] = useState<WorkspaceTab>('controls');
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  const fwId = framework.id;

  // Real-data: A3 framework_controls + framework_attestations → Control VM.
  const { data: realControls = [] } = useFrameworkControls(fwId);
  const relatedControls = realControls;
  const { data: allEvidence = [] } = useTrustEvidence();
  const relatedEvidence = useMemo(
    () => allEvidence.filter(e => e.controlMappings.some(m => m.framework === fwId)),
    [allEvidence, fwId],
  );
  const reports = useMemo(() => generateMockReports(framework), [framework]);

  // Trust bug #15: POA&M items now persist to poam_items via the real
  // /governance/frameworks/:id/poam endpoints (was local-state mock that
  // reverted on reload). `poam` is the server list; RoadmapTab still drives
  // the UI via its array `onChange`, which we diff into create/update/delete
  // mutations here so every edit actually persists.
  const { data: poam = [] } = useTrustPoam(fwId);
  const createPoamItem = useCreatePoamItem(fwId);
  const updatePoamItem = useUpdatePoamItem(fwId);
  const deletePoamItem = useDeletePoamItem(fwId);
  const toPoamPayload = (i: PoamItem): PoamWritePayload => ({
    title: i.name,
    description: i.description,
    status: i.status,
    ownerName: i.owner,
    ownerUserId: null,
    controlCode: null,
    targetCloseDate: i.targetCloseDate,
    milestones: i.milestones,
  });
  const poamChanged = (a: PoamItem, b: PoamItem) =>
    a.name !== b.name || a.description !== b.description || a.status !== b.status ||
    a.owner !== b.owner || a.targetCloseDate !== b.targetCloseDate ||
    JSON.stringify(a.milestones) !== JSON.stringify(b.milestones);
  const handlePoamChange = (next: PoamItem[]) => {
    const prevById = new Map(poam.map((i) => [i.id, i as unknown as PoamItem]));
    const nextIds = new Set(next.map((i) => i.id));
    for (const item of next) {
      const prev = prevById.get(item.id);
      if (!prev) createPoamItem.mutate(toPoamPayload(item));
      else if (poamChanged(prev, item)) updatePoamItem.mutate({ id: item.id, patch: toPoamPayload(item) });
    }
    for (const item of poam) {
      if (!nextIds.has(item.id)) deletePoamItem.mutate({ id: item.id });
    }
  };

  const wizardSteps = WIZARD_STEPS[fwId] ?? [];
  const isFederalFramework = fwId === 'cmmc_l2' || fwId === 'itar';

  const statusStyle = FRAMEWORK_STATUS_STYLES[framework.status];
  const categoryStyle = FRAMEWORK_CATEGORY_STYLES[framework.category];
  const riskStyle = RISK_RATING_STYLES[framework.riskRating];

  const daysToAssessment = daysUntil(framework.nextAssessmentDate);
  const daysToCertExpiry = daysUntil(framework.certificationExpiry);

  const passRate = framework.controlsPassing + framework.controlsFailing > 0
    ? Math.round((framework.controlsPassing / (framework.controlsPassing + framework.controlsFailing)) * 100)
    : 0;

  return (
    <div className="p-6 space-y-6">
      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Link
            {...({ to: '/trust/frameworks' } as any)}
            className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            <span>Frameworks</span>
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground font-medium">{framework.name}</span>
        </div>

        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-start gap-3">
              <div
                className={cn(
                  'h-12 w-12 rounded-xl flex items-center justify-center shrink-0 border-2',
                  framework.iconColor === 'red' && 'bg-red-500/10 border-red-500/20 text-red-600 dark:text-red-400',
                  framework.iconColor === 'blue' && 'bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-400',
                  framework.iconColor === 'emerald' && 'bg-emerald-500/10 border-emerald-500/20 text-emerald-600 dark:text-emerald-400',
                  framework.iconColor === 'violet' && 'bg-violet-500/10 border-violet-500/20 text-violet-600 dark:text-violet-400',
                  framework.iconColor === 'amber' && 'bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-400',
                  framework.iconColor === 'pink' && 'bg-pink-500/10 border-pink-500/20 text-pink-600 dark:text-pink-400',
                  framework.iconColor === 'indigo' && 'bg-indigo-500/10 border-indigo-500/20 text-indigo-600 dark:text-indigo-400',
                  framework.iconColor === 'sky' && 'bg-sky-500/10 border-sky-500/20 text-sky-600 dark:text-sky-400',
                  framework.iconColor === 'cyan' && 'bg-cyan-500/10 border-cyan-500/20 text-cyan-600 dark:text-cyan-400',
                  !['red', 'blue', 'emerald', 'violet', 'amber', 'pink', 'indigo', 'sky', 'cyan'].includes(framework.iconColor)
                    && 'bg-primary/10 border-primary/20 text-primary',
                )}
              >
                <ShieldCheck className="h-6 w-6" />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-2xl font-semibold tracking-tight">{framework.name}</h1>
                  <Badge variant="outline" className={cn('text-[10px]', statusStyle.color)}>
                    {statusStyle.label}
                  </Badge>
                  <Badge variant="outline" className={cn('text-[10px]', categoryStyle.color)}>
                    {categoryStyle.label}
                  </Badge>
                  <Badge variant="outline" className={cn('text-[10px]', riskStyle.color)}>
                    {riskStyle.label} Risk
                  </Badge>
                  {framework.recentChanges > 0 && (
                    <Badge
                      variant="outline"
                      className="text-[10px] border-blue-500/20 text-blue-600 dark:text-blue-400"
                      title={`${framework.recentChanges} recent changes`}
                    >
                      {framework.recentChanges} new
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1.5">
                  <span className="font-medium text-foreground">{framework.fullName}</span>
                </p>
                <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
                  {framework.description}
                </p>
                <div className="flex items-center gap-4 mt-2 text-[11px] text-muted-foreground flex-wrap">
                  <span className="inline-flex items-center gap-1">
                    <Building2 className="h-3 w-3" />
                    {framework.authority}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Flag className="h-3 w-3" />
                    {framework.jurisdiction}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Layers className="h-3 w-3" />
                    {framework.controlCount} controls
                  </span>
                  {framework.enforcementDate && (
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Enforcement: {formatDate(framework.enforcementDate)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap shrink-0">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => toast.success('Continuous assessment scheduled', {
                description: `Controls will be re-tested within 15 minutes for ${framework.name}.`,
              })}
            >
              <RefreshCw className="h-3.5 w-3.5" /> Run Assessment
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setActiveTab('setup')}
            >
              <Settings className="h-3.5 w-3.5" /> Edit Setup
            </Button>
            <Button
              size="sm"
              className="gap-1.5"
              onClick={() => setReportDialogOpen(true)}
            >
              <FileText className="h-3.5 w-3.5" /> Generate Audit Report
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="px-2" title="More framework actions">
                  <Settings className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{framework.shortName} actions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => toast.success('Framework ownership change dialog opened')}
                >
                  Reassign Owner
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => toast.success('Assessor update dialog opened')}
                >
                  Update Assessor
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => toast.success('Archive opened', { description: 'Past assessments and reports remain accessible.' })}
                >
                  View Archive
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Setup progress bar */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">Framework Setup Progress</span>
            <span className={cn('text-xs font-semibold', progressTextColor(framework.setupProgress))}>
              {framework.setupProgress}%
              {framework.setupProgress < 100 && (
                <span className="text-muted-foreground ml-1.5">
                  ({wizardSteps.length > 0 ? `${Math.ceil(wizardSteps.length * framework.setupProgress / 100)}/${wizardSteps.length} steps` : 'in progress'})
                </span>
              )}
            </span>
          </div>
          <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
            <div
              className={cn('h-full transition-all duration-500', progressColor(framework.setupProgress))}
              style={{ width: `${framework.setupProgress}%` }}
            />
          </div>
        </div>
      </div>

      {/* ── Stats Row ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {/* Controls */}
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Controls Health</span>
              <Target className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="flex items-baseline gap-1">
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                {framework.controlsPassing}
              </p>
              <p className="text-sm text-muted-foreground">/ {framework.controlCount}</p>
            </div>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {passRate}% pass rate
              {framework.controlsFailing > 0 && (
                <span className="text-red-600 dark:text-red-400 ml-1.5">
                  · {framework.controlsFailing} failing
                </span>
              )}
            </p>
          </CardContent>
        </Card>

        {/* Evidence */}
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Evidence</span>
              <FolderArchive className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex items-baseline gap-1">
              <p className="text-2xl font-bold">{framework.evidenceFresh}</p>
              <p className="text-sm text-muted-foreground">/ {framework.evidenceCount}</p>
            </div>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              fresh
              {framework.evidenceStale > 0 && (
                <span className="text-amber-600 dark:text-amber-400 ml-1.5">
                  · {framework.evidenceStale} stale
                </span>
              )}
            </p>
          </CardContent>
        </Card>

        {/* Open Findings */}
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Open Findings</span>
              <AlertTriangle className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />
            </div>
            <p className={cn(
              'text-2xl font-bold',
              framework.openFindings > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-emerald-600 dark:text-emerald-400',
            )}>
              {framework.openFindings}
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {framework.openFindings > 0 ? 'Require remediation' : 'No open findings'}
            </p>
          </CardContent>
        </Card>

        {/* Last Assessment */}
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Last Assessment</span>
              <Clock className={cn(
                'h-3.5 w-3.5',
                daysToAssessment !== null && daysToAssessment <= 60
                  ? 'text-orange-600 dark:text-orange-400'
                  : 'text-muted-foreground',
              )} />
            </div>
            <p className="text-sm font-semibold truncate">
              {formatDate(framework.lastAssessmentDate)}
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Next: {formatDate(framework.nextAssessmentDate)}
              {daysToAssessment !== null && (
                <span className={cn(
                  'ml-1',
                  daysToAssessment <= 60 && 'text-orange-600 dark:text-orange-400 font-medium',
                )}>
                  ({daysToAssessment > 0 ? `${daysToAssessment}d` : daysToAssessment === 0 ? 'today' : `${Math.abs(daysToAssessment)}d overdue`})
                </span>
              )}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Secondary facts row */}
      {(framework.assessor || framework.certificationExpiry) && (
        <div className="flex items-center gap-4 text-xs flex-wrap">
          {framework.assessor && (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border bg-muted/40">
              <User className="h-3 w-3 text-muted-foreground" />
              <span className="text-muted-foreground">Assessor:</span>
              <span className="font-medium">{framework.assessor}</span>
            </div>
          )}
          {framework.certificationExpiry && (
            <div className={cn(
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded border',
              daysToCertExpiry !== null && daysToCertExpiry <= 90
                ? 'bg-orange-500/10 border-orange-500/20 text-orange-600 dark:text-orange-400'
                : 'bg-muted/40',
            )}>
              <ShieldCheck className="h-3 w-3" />
              <span className="text-muted-foreground">Certified until:</span>
              <span className="font-medium">{formatDate(framework.certificationExpiry)}</span>
              {daysToCertExpiry !== null && (
                <span className="text-[10px]">({daysToCertExpiry}d)</span>
              )}
            </div>
          )}
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border bg-muted/40">
            <RefreshCw className="h-3 w-3 text-muted-foreground" />
            <span className="text-muted-foreground">Update cadence:</span>
            <span className="font-medium">{framework.updateFrequency}</span>
          </div>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border bg-muted/40">
            <FolderArchive className="h-3 w-3 text-muted-foreground" />
            <span className="text-muted-foreground">Retention:</span>
            <span className="font-medium">{framework.retentionYears} years</span>
          </div>
        </div>
      )}

      {/* ── Tabs ──────────────────────────────────────────────────────── */}
      <Tabs value={activeTab} onValueChange={v => setActiveTab(v as WorkspaceTab)}>
        <TabsList className="h-10 p-1 bg-muted flex-wrap">
          <TabsTrigger value="controls" className="gap-1.5 text-xs">
            <Target className="h-3.5 w-3.5" /> Controls
            <span className="text-[10px] opacity-70">({relatedControls.length})</span>
          </TabsTrigger>
          <TabsTrigger value="evidence" className="gap-1.5 text-xs">
            <FolderArchive className="h-3.5 w-3.5" /> Evidence
            <span className="text-[10px] opacity-70">({relatedEvidence.length})</span>
          </TabsTrigger>
          <TabsTrigger value="setup" className="gap-1.5 text-xs">
            <Settings className="h-3.5 w-3.5" /> Setup Wizard
          </TabsTrigger>
          <TabsTrigger value="reports" className="gap-1.5 text-xs">
            <FileText className="h-3.5 w-3.5" /> Reports
            <span className="text-[10px] opacity-70">({reports.length})</span>
          </TabsTrigger>
          <TabsTrigger value="roadmap" className="gap-1.5 text-xs">
            <Flag className="h-3.5 w-3.5" />
            {isFederalFramework ? 'POA&M' : 'Improvement Plan'}
            <span className="text-[10px] opacity-70">({poam.length})</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="controls" className="mt-4">
          <ControlsTab controls={relatedControls} fwId={fwId} />
        </TabsContent>

        <TabsContent value="evidence" className="mt-4">
          <EvidenceTab evidence={relatedEvidence} />
        </TabsContent>

        <TabsContent value="setup" className="mt-4">
          <SetupWizardTab framework={framework} steps={wizardSteps} />
        </TabsContent>

        <TabsContent value="reports" className="mt-4">
          <ReportsTab
            reports={reports}
            framework={framework}
            onGenerate={() => setReportDialogOpen(true)}
          />
        </TabsContent>

        <TabsContent value="roadmap" className="mt-4">
          <RoadmapTab
            items={poam as unknown as PoamItem[]}
            federal={isFederalFramework}
            controls={relatedControls.map(c => ({ id: c.id, name: c.name }))}
            onChange={handlePoamChange}
          />
        </TabsContent>

      </Tabs>

      {/* Report Generation Dialog */}
      <GenerateReportDialog
        framework={framework}
        open={reportDialogOpen}
        onOpenChange={setReportDialogOpen}
      />

    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── Controls Tab ────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

function ControlsTab({ controls, fwId }: { controls: Control[]; fwId: FrameworkId }) {
  const reassignOwner = useReassignControlOwner(fwId);
  const setControlStatus = useSetControlStatus(fwId);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | Control['status']>('all');
  const [selectedControl, setSelectedControl] = useState<Control | null>(null);
  const [reassigningControl, setReassigningControl] = useState<Control | null>(null);
  const [controlOverrides, setControlOverrides] = useState<Record<string, { ownerName?: string; status?: Control['status'] }>>({});

  const applyOverrides = (c: Control): Control => {
    const override = controlOverrides[c.id];
    if (!override) return c;
    return { ...c, ...override };
  };

  const handleOpenDetail = (control: Control) => {
    setSelectedControl(applyOverrides(control));
  };

  const handleReassign = (control: Control) => {
    setSelectedControl(null);
    setReassigningControl(applyOverrides(control));
  };

  const handleReassignSubmit = (controlId: string, newOwnerId: string, newOwnerName: string, _reason: string) => {
    const ctrl = controls.find((c) => c.id === controlId);
    const controlCode = (ctrl as unknown as { controlId?: string })?.controlId;
    if (!controlCode) { toast.error('Could not resolve control'); return; }
    reassignOwner.mutate(
      { controlCode, ownerUserId: newOwnerId || null, ownerName: newOwnerName },
      {
        onSuccess: () => toast.success('Control owner reassigned', { description: `${ctrl?.name ?? controlCode} → ${newOwnerName}` }),
        onError: (e) => toast.error('Reassign failed', { description: e.message }),
      },
    );
  };

  const handleStatusChange = (controlId: string, status: Control['status']) => {
    const ctrl = controls.find((c) => c.id === controlId);
    const controlCode = (ctrl as unknown as { controlId?: string })?.controlId;
    if (!controlCode) { toast.error('Could not resolve control'); return; }
    setControlStatus.mutate(
      { controlCode, status },
      {
        onSuccess: () => toast.success('Control status updated', { description: `${ctrl?.name ?? controlCode}: ${status}` }),
        onError: (e) => toast.error('Status update failed', { description: e.message }),
      },
    );
  };

  const filtered = useMemo(() => {
    return controls.map(applyOverrides).filter(c => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return c.name.toLowerCase().includes(q)
        || c.description.toLowerCase().includes(q)
        || c.ownerName.toLowerCase().includes(q)
        || c.mappings.some(m => m.controlId.toLowerCase().includes(q));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controls, search, statusFilter, controlOverrides]);

  const { sorted, sortConfig, handleSort } = useTableSort(filtered, 'name');

  if (controls.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center border rounded-lg bg-muted/20">
        <Target className="h-10 w-10 text-muted-foreground/20 mb-3" />
        <p className="text-sm font-medium">No controls mapped to this framework yet</p>
        <p className="text-xs text-muted-foreground mt-1">
          Controls will appear here as they are added to the control catalog.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <Input
          placeholder="Search controls, owners, control IDs..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="max-w-sm"
        />
        <Select value={statusFilter} onValueChange={v => setStatusFilter(v as typeof statusFilter)}>
          <SelectTrigger className="w-40 h-9 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="passing">Passing</SelectItem>
            <SelectItem value="failing">Failing</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
            <SelectItem value="needs_review">Needs Review</SelectItem>
            <SelectItem value="not_applicable">Not Applicable</SelectItem>
            <SelectItem value="not_implemented">Not Implemented</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground ml-auto">
          {filtered.length} of {controls.length}
        </span>
      </div>

      <div className="rounded-lg border bg-card shadow-sm overflow-x-auto">
        <div className="min-w-[1500px]">
          <div className="grid grid-cols-[minmax(320px,2fr)_140px_110px_100px_110px_140px_130px_130px_110px_80px] gap-3 px-4 py-3 bg-muted/50 text-xs uppercase tracking-wider border-b">
            <SortableHeader label="Control" sortKey="name" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Family" sortKey="family" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Status" sortKey="status" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Automation" sortKey="automation" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Priority" sortKey="priority" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Owner" sortKey="ownerName" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Last Tested" sortKey="lastTestedAt" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Next Review" sortKey="nextReviewAt" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Pass/Fail 30d" sortKey="passCount30d" currentSort={sortConfig} onSort={handleSort} />
            <span className="text-muted-foreground text-right text-xs">Actions</span>
          </div>

          <div className="divide-y">
            {sorted.map(control => {
              const mapping = control.mappings.find(m => m.framework === fwId);
              const statusStyle = CONTROL_STATUS_STYLES[control.status];
              const automationStyle = AUTOMATION_STYLES[control.automation];
              const priorityStyle = PRIORITY_STYLES[control.priority];
              const total30d = control.passCount30d + control.failureCount30d;
              const passPct30d = total30d > 0 ? Math.round((control.passCount30d / total30d) * 100) : 0;
              const AutomationIcon = automationStyle.icon;

              return (
                <div
                  key={control.id}
                  className="grid grid-cols-[minmax(320px,2fr)_140px_110px_100px_110px_140px_130px_130px_110px_80px] gap-3 px-4 py-3 items-center hover:bg-muted/20 transition-colors cursor-pointer"
                  onClick={() => handleOpenDetail(control)}
                >
                  {/* Name + description */}
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium truncate">{control.name}</p>
                      {control.argusAiPrimitive && (
                        <span title={`Auto-collected via ${control.argusAiPrimitive}`}>
                          <Sparkles className="h-3 w-3 text-violet-600 dark:text-violet-400 shrink-0" />
                        </span>
                      )}
                    </div>
                    {mapping && (
                      <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                        <code className="text-[10px] px-1 py-0.5 rounded bg-muted/60 mr-1">{mapping.controlId}</code>
                        {mapping.controlName}
                      </p>
                    )}
                    <p className="text-[11px] text-muted-foreground/70 truncate mt-0.5">
                      {control.description}
                    </p>
                  </div>

                  {/* Family */}
                  <span className="text-xs text-muted-foreground truncate" title={familyLabel(control.family)}>
                    {familyLabel(control.family)}
                  </span>

                  {/* Status */}
                  <Badge variant="outline" className={cn('text-[10px] w-fit', statusStyle.color)}>
                    {statusStyle.label}
                  </Badge>

                  {/* Automation */}
                  <Badge variant="outline" className={cn('text-[10px] gap-1 w-fit', automationStyle.color)}>
                    <AutomationIcon className="h-2.5 w-2.5" />
                    {automationStyle.label}
                  </Badge>

                  {/* Priority */}
                  <Badge variant="outline" className={cn('text-[10px] w-fit', priorityStyle.color)}>
                    {priorityStyle.label}
                  </Badge>

                  {/* Owner */}
                  <div className="flex items-center gap-1.5 min-w-0">
                    <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-[9px] font-semibold shrink-0">
                      {control.ownerName.split(' ').map(p => p[0]).join('').slice(0, 2)}
                    </div>
                    <span className="text-xs truncate">{control.ownerName}</span>
                  </div>

                  {/* Last tested */}
                  <div className="text-xs">
                    <span className="text-muted-foreground">{formatDate(control.lastTestedAt)}</span>
                  </div>

                  {/* Next review */}
                  <div className="text-xs">
                    <span className="text-muted-foreground">{formatDate(control.nextReviewAt)}</span>
                  </div>

                  {/* Pass/Fail 30d */}
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-1 text-[11px]">
                      <CheckCircle2 className="h-2.5 w-2.5 text-emerald-500" />
                      <span className="font-medium">{control.passCount30d}</span>
                      <span className="text-muted-foreground">/</span>
                      {control.failureCount30d > 0 ? (
                        <>
                          <AlertTriangle className="h-2.5 w-2.5 text-red-500" />
                          <span className="text-red-600 dark:text-red-400 font-medium">{control.failureCount30d}</span>
                        </>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </div>
                    <span className={cn('text-[10px]', progressTextColor(passPct30d))}>
                      {passPct30d}% pass
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      title="Run test now"
                      onClick={() => toast.info(`Running test for ${control.name}...`)}
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="More actions">
                          <ChevronRight className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => handleOpenDetail(control)}>
                          <FileText className="h-3.5 w-3.5 mr-2" /> View details
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => setReassigningControl(control)}>
                          <User className="h-3.5 w-3.5 mr-2" /> Reassign owner
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => toast.success('Test executed')}>
                          <Zap className="h-3.5 w-3.5 mr-2" /> Run test
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={() => toast.info('Evidence attached')}>
                          <FolderArchive className="h-3.5 w-3.5 mr-2" /> Attach evidence
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <ControlDetailSheet
        control={selectedControl}
        frameworkId={fwId}
        onClose={() => setSelectedControl(null)}
        onReassign={handleReassign}
        onStatusChange={handleStatusChange}
      />

      <ReassignControlDialog
        control={reassigningControl}
        open={!!reassigningControl}
        onOpenChange={(o) => { if (!o) setReassigningControl(null); }}
        onReassign={handleReassignSubmit}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── Evidence Tab ────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

function EvidenceTab({ evidence }: { evidence: Evidence[] }) {
  const [search, setSearch] = useState('');
  const [detailEvidence, setDetailEvidence] = useState<Evidence | null>(null);

  const filtered = useMemo(() => {
    if (!search) return evidence;
    const q = search.toLowerCase();
    return evidence.filter(e =>
      e.title.toLowerCase().includes(q)
      || e.description.toLowerCase().includes(q)
      || e.ownerName.toLowerCase().includes(q)
      || e.tags.some(t => t.toLowerCase().includes(q)),
    );
  }, [evidence, search]);

  const { sorted, sortConfig, handleSort } = useTableSort(filtered, 'uploadedAt', 'desc');

  if (evidence.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center border rounded-lg bg-muted/20">
        <FolderArchive className="h-10 w-10 text-muted-foreground/20 mb-3" />
        <p className="text-sm font-medium">No evidence mapped to this framework yet</p>
        <p className="text-xs text-muted-foreground mt-1">
          Upload or auto-collect evidence to support this framework's controls.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <Input
          placeholder="Search evidence, descriptions, tags, owner..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="max-w-sm"
        />
        <span className="text-xs text-muted-foreground ml-auto">
          {filtered.length} of {evidence.length}
        </span>
      </div>

      <div className="rounded-lg border bg-card shadow-sm overflow-x-auto">
        <div className="min-w-[1400px]">
          <div className="grid grid-cols-[minmax(340px,2.2fr)_110px_150px_130px_120px_120px_90px_70px] gap-3 px-4 py-3 bg-muted/50 text-xs uppercase tracking-wider border-b">
            <SortableHeader label="Title" sortKey="title" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Type" sortKey="type" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Source" sortKey="source" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Owner" sortKey="ownerName" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Effective" sortKey="effectiveDate" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Expires" sortKey="expirationDate" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Version" sortKey="versionNumber" currentSort={sortConfig} onSort={handleSort} />
            <span className="text-right text-xs text-muted-foreground">Actions</span>
          </div>

          <div className="divide-y">
            {sorted.map(ev => {
              const isArgus = ev.source === 'argus_primitive' || !!ev.argusAiPrimitive;
              const isAutoCollected = ev.source === 'auto_collected' || ev.source === 'api_pull' || isArgus;
              const daysToExpiry = daysUntil(ev.expirationDate);
              const expiringSoon = daysToExpiry !== null && daysToExpiry >= 0 && daysToExpiry <= 30;
              const expired = daysToExpiry !== null && daysToExpiry < 0;

              return (
                <div
                  key={ev.id}
                  className="grid grid-cols-[minmax(340px,2.2fr)_110px_150px_130px_120px_120px_90px_70px] gap-3 px-4 py-3 items-center hover:bg-muted/20 transition-colors"
                >
                  {/* Title */}
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium truncate">{ev.title}</p>
                      {isArgus && (
                        <Badge
                          variant="outline"
                          className="text-[9px] px-1 py-0 border-violet-500/20 text-violet-600 dark:text-violet-400 bg-violet-500/5 gap-0.5"
                          title={ev.argusAiPrimitive ? `ArgusAI primitive: ${ev.argusAiPrimitive}` : 'Argus auto-collected'}
                        >
                          <Sparkles className="h-2.5 w-2.5" />
                          Auto
                        </Badge>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                      {ev.description}
                    </p>
                    {ev.tags.length > 0 && (
                      <div className="flex items-center gap-1 mt-1 flex-wrap">
                        {ev.tags.slice(0, 3).map(t => (
                          <span key={t} className="text-[9px] px-1 py-0 rounded bg-muted text-muted-foreground">
                            {t}
                          </span>
                        ))}
                        {ev.tags.length > 3 && (
                          <span className="text-[9px] text-muted-foreground">+{ev.tags.length - 3}</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Type */}
                  <Badge variant="outline" className="text-[10px] w-fit">
                    {ev.type}
                  </Badge>

                  {/* Source */}
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-[10px] gap-1 w-fit',
                        isAutoCollected
                          ? 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20'
                          : 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20',
                      )}
                    >
                      {isAutoCollected && <Sparkles className="h-2.5 w-2.5" />}
                      {ev.source.replace('_', ' ')}
                    </Badge>
                  </div>

                  {/* Owner */}
                  <div className="flex items-center gap-1.5 min-w-0">
                    <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-[9px] font-semibold shrink-0">
                      {ev.ownerName.split(' ').map(p => p[0]).join('').slice(0, 2)}
                    </div>
                    <span className="text-xs truncate">{ev.ownerName}</span>
                  </div>

                  {/* Effective */}
                  <span className="text-xs text-muted-foreground">{formatDate(ev.effectiveDate)}</span>

                  {/* Expires */}
                  <div className="text-xs">
                    <span className={cn(
                      expired && 'text-red-600 dark:text-red-400 font-medium',
                      expiringSoon && 'text-amber-600 dark:text-amber-400 font-medium',
                      !expired && !expiringSoon && 'text-muted-foreground',
                    )}>
                      {formatDate(ev.expirationDate)}
                    </span>
                    {daysToExpiry !== null && (
                      <p className="text-[9px] text-muted-foreground">
                        {daysToExpiry > 0 ? `${daysToExpiry}d` : daysToExpiry === 0 ? 'today' : `${Math.abs(daysToExpiry)}d ago`}
                      </p>
                    )}
                  </div>

                  {/* Version */}
                  <span className="text-xs text-muted-foreground">v{ev.versionNumber}</span>

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-0.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      title="Download"
                      onClick={() => toast.success(`Downloading ${ev.fileName ?? ev.title}...`)}
                    >
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      title="Open details"
                      onClick={() => setDetailEvidence(ev)}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <EvidenceDetailSheet
        evidence={detailEvidence}
        onClose={() => setDetailEvidence(null)}
      />
    </div>
  );
}

// ─── Evidence Detail Sheet ───────────────────────────────────────────────────

function EvidenceDetailSheet({ evidence, onClose }: { evidence: Evidence | null; onClose: () => void }) {
  if (!evidence) return null;
  const expired = evidence.expirationDate ? new Date(evidence.expirationDate) < new Date() : false;
  return (
    <Sheet open={!!evidence} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="sm:max-w-xl w-full overflow-y-auto p-0">
        <div className="p-6 pb-4 border-b">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <Badge variant="outline" className="text-[10px] capitalize">{evidence.type}</Badge>
                <Badge variant="outline" className="text-[10px] capitalize">{evidence.source.replace(/_/g, ' ')}</Badge>
                <Badge variant="outline" className={cn('text-[10px] capitalize', expired ? 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30' : '')}>
                  {evidence.status.replace(/_/g, ' ')}
                </Badge>
                <span className="text-[10px] text-muted-foreground">v{evidence.versionNumber}</span>
              </div>
              <h3 className="text-sm font-semibold">{evidence.title}</h3>
              <p className="text-xs text-muted-foreground mt-1">{evidence.description}</p>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onClose} aria-label="Close" title="Close">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <MetaCell label="Owner" value={evidence.ownerName} />
            <MetaCell label="Uploaded by" value={evidence.uploadedByName} />
            <MetaCell label="Uploaded at" value={new Date(evidence.uploadedAt).toLocaleString()} />
            <MetaCell label="Effective" value={new Date(evidence.effectiveDate).toLocaleDateString()} />
            <MetaCell label="Expires" value={evidence.expirationDate ? new Date(evidence.expirationDate).toLocaleDateString() : '—'} />
            <MetaCell label="Review cycle" value={<span className="capitalize">{evidence.reviewCycle}</span>} />
            {evidence.fileName && <MetaCell label="File name" value={<code className="text-[10px]">{evidence.fileName}</code>} />}
            {evidence.fileSize !== null && <MetaCell label="Size" value={`${Math.round(evidence.fileSize / 1024)} KB`} />}
          </div>

          {evidence.tags.length > 0 && (
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-1.5">Tags</p>
              <div className="flex flex-wrap gap-1">
                {evidence.tags.map(t => (
                  <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-1.5">Framework mappings</p>
            <div className="space-y-1.5">
              {evidence.controlMappings.map(m => (
                <div key={`${m.framework}-${m.controlId}`} className="flex items-center justify-between text-xs rounded-md border p-2">
                  <code className="text-[11px] font-mono">{m.controlId}</code>
                  <span className="text-muted-foreground uppercase text-[10px] tracking-wider">
                    {m.framework.replace(/_/g, ' ')}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-1.5">Chain of custody</p>
            <div className="space-y-1">
              {evidence.chainOfCustody.slice(0, 5).map((entry, i) => (
                <div key={i} className="text-[11px] flex items-start gap-2 border rounded-md p-2">
                  <Clock className="h-3 w-3 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p><span className="font-medium">{entry.userName}</span> {entry.action}</p>
                    <p className="text-[10px] text-muted-foreground">{new Date(entry.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {evidence.argusAiPrimitive && (
            <div className="rounded-md border-l-4 border-violet-500 bg-violet-50 dark:bg-violet-950/30 p-3">
              <p className="text-[10px] text-violet-700 dark:text-violet-300 uppercase tracking-wider font-semibold mb-0.5">
                Auto-collected via ArgusAI primitive
              </p>
              <code className="text-xs text-violet-900 dark:text-violet-200">{evidence.argusAiPrimitive}</code>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              size="sm"
              variant="outline"
              className="flex-1"
              onClick={() => toast.success(`Downloading ${evidence.fileName ?? evidence.title}...`)}
            >
              <Download className="h-3.5 w-3.5 mr-1.5" /> Download
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(evidence.id);
                toast.success('Evidence ID copied');
              }}
            >
              <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy ID
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function MetaCell({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-md border p-2.5">
      <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-0.5">{label}</div>
      <div className="text-xs font-medium">{value}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── Setup Wizard Tab ────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

function SetupWizardTab({ framework, steps }: { framework: Framework; steps: WizardStep[] }) {
  const [currentStep, setCurrentStep] = useState(() => {
    // Start at step reflecting actual progress
    return Math.min(Math.floor((framework.setupProgress / 100) * steps.length), Math.max(steps.length - 1, 0));
  });
  const [answers, setAnswers] = useState<Record<string, string | boolean>>(() => {
    const init: Record<string, string | boolean> = {};
    steps.forEach(s => s.questions.forEach(q => {
      if (q.defaultValue !== undefined) init[q.id] = q.defaultValue;
    }));
    return init;
  });

  if (steps.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center border rounded-lg bg-muted/20">
        <Settings className="h-10 w-10 text-muted-foreground/20 mb-3" />
        <p className="text-sm font-medium">No setup wizard defined for this framework yet</p>
        <p className="text-xs text-muted-foreground mt-1">
          Framework setup questions will appear here once configured.
        </p>
      </div>
    );
  }

  const step = steps[currentStep]!;
  const answeredInStep = step.questions.filter(q => {
    const a = answers[q.id];
    return a !== undefined && a !== '' && a !== false;
  }).length;

  const totalQuestions = steps.reduce((sum, s) => sum + s.questions.length, 0);
  const totalAnswered = steps.reduce((sum, s) => sum + s.questions.filter(q => {
    const a = answers[q.id];
    return a !== undefined && a !== '' && a !== false;
  }).length, 0);

  const handleChange = (id: string, value: string | boolean) => {
    setAnswers(prev => ({ ...prev, [id]: value }));
  };

  const isLast = currentStep === steps.length - 1;

  return (
    <div className="grid grid-cols-12 gap-6">
      {/* Left: step navigator */}
      <div className="col-span-12 lg:col-span-3 space-y-3">
        <Card>
          <CardContent className="pt-4 pb-3 space-y-3">
            <div>
              <p className="text-xs text-muted-foreground">Overall progress</p>
              <p className={cn('text-xl font-bold', progressTextColor(framework.setupProgress))}>
                {framework.setupProgress}%
              </p>
              <Progress value={framework.setupProgress} className="h-1.5 mt-1" />
            </div>
            <Separator />
            <div className="text-xs space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Answered</span>
                <span className="font-semibold">{totalAnswered}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Remaining</span>
                <span className="font-semibold">{totalQuestions - totalAnswered}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Steps</span>
                <span className="font-semibold">{currentStep + 1} / {steps.length}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="rounded-lg border bg-card">
          <div className="px-3 py-2 border-b bg-muted/30 text-[11px] uppercase tracking-wider text-muted-foreground">
            Setup Steps
          </div>
          <div className="p-1.5 space-y-0.5 max-h-[420px] overflow-y-auto">
            {steps.map((s, idx) => {
              const isActive = idx === currentStep;
              const isComplete = idx < currentStep;
              return (
                <button
                  key={s.id}
                  onClick={() => setCurrentStep(idx)}
                  className={cn(
                    'w-full text-left px-2.5 py-2 rounded-md transition-colors flex items-center gap-2 text-xs',
                    isActive && 'bg-primary/10 text-primary',
                    !isActive && isComplete && 'text-muted-foreground hover:bg-muted/50',
                    !isActive && !isComplete && 'text-muted-foreground hover:bg-muted/50',
                  )}
                >
                  <div className={cn(
                    'h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-semibold shrink-0 border',
                    isActive && 'bg-primary text-primary-foreground border-primary',
                    !isActive && isComplete && 'bg-emerald-500 text-white border-emerald-500',
                    !isActive && !isComplete && 'bg-muted border-muted-foreground/30',
                  )}>
                    {isComplete ? <Check className="h-3 w-3" /> : idx + 1}
                  </div>
                  <span className={cn('truncate', isActive && 'font-medium')}>{s.title}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right: step form */}
      <div className="col-span-12 lg:col-span-9 space-y-4">
        <Card>
          <CardContent className="pt-5 pb-5 space-y-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground uppercase tracking-wider">
                  <span>Step {currentStep + 1} of {steps.length}</span>
                </div>
                <h3 className="text-lg font-semibold mt-1">{step.title}</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{step.description}</p>
              </div>
              <Badge
                variant="outline"
                className={cn(
                  'text-[11px]',
                  answeredInStep === step.questions.length
                    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20'
                    : 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
                )}
              >
                {answeredInStep} / {step.questions.length} answered
              </Badge>
            </div>

            <Separator />

            <div className="space-y-5">
              {step.questions.map(q => (
                <div key={q.id} className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor={q.id} className="text-sm font-medium">
                      {q.label}
                      {q.required && <span className="text-red-500 ml-0.5">*</span>}
                    </Label>
                  </div>
                  {q.helpText && (
                    <p className="text-xs text-muted-foreground">{q.helpText}</p>
                  )}
                  {q.kind === 'text' && (
                    <Input
                      id={q.id}
                      value={(answers[q.id] ?? '') as string}
                      onChange={e => handleChange(q.id, e.target.value)}
                      className="h-9"
                    />
                  )}
                  {q.kind === 'date' && (
                    <Input
                      id={q.id}
                      type="date"
                      value={(answers[q.id] ?? '') as string}
                      onChange={e => handleChange(q.id, e.target.value)}
                      className="h-9 w-56"
                    />
                  )}
                  {q.kind === 'textarea' && (
                    <Textarea
                      id={q.id}
                      value={(answers[q.id] ?? '') as string}
                      onChange={e => handleChange(q.id, e.target.value)}
                      rows={3}
                      className="resize-none"
                    />
                  )}
                  {q.kind === 'select' && q.options && (
                    <Select
                      value={(answers[q.id] ?? '') as string}
                      onValueChange={v => handleChange(q.id, v)}
                    >
                      <SelectTrigger className="h-9 max-w-md">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {q.options.map(o => (
                          <SelectItem key={o} value={o}>{o}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {q.kind === 'checkbox' && (
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={q.id}
                        checked={!!answers[q.id]}
                        onCheckedChange={v => handleChange(q.id, !!v)}
                      />
                      <Label htmlFor={q.id} className="text-sm cursor-pointer font-normal text-muted-foreground">
                        {answers[q.id] ? 'Confirmed' : 'Not yet confirmed'}
                      </Label>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-between gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={currentStep === 0}
            onClick={() => setCurrentStep(s => Math.max(s - 1, 0))}
            className="gap-1.5"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Previous
          </Button>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => toast.success('Answers saved', {
                description: `Progress saved for ${framework.name} setup.`,
              })}
            >
              <Check className="h-3.5 w-3.5" /> Save Progress
            </Button>
            {isLast ? (
              <Button
                size="sm"
                className="gap-1.5"
                onClick={() => toast.success('Generating compliance manual...', {
                  description: `Your ${framework.name} compliance manual will be emailed when ready.`,
                })}
              >
                <FileCheck className="h-3.5 w-3.5" /> Generate Compliance Manual
              </Button>
            ) : (
              <Button
                size="sm"
                className="gap-1.5"
                onClick={() => setCurrentStep(s => Math.min(s + 1, steps.length - 1))}
              >
                Next <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── Reports Tab ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

function ReportsTab({
  reports,
  framework,
  onGenerate,
}: {
  reports: AuditReport[];
  framework: Framework;
  onGenerate: () => void;
}) {
  const { sorted, sortConfig, handleSort } = useTableSort(reports, 'generatedDate', 'desc');
  const isFederal = framework.id === 'cmmc_l2' || framework.id === 'fedramp' as any;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-muted-foreground">
          Generated audit reports and exports for {framework.name}.
          {isFederal && ' OSCAL export available for federal submissions.'}
        </p>
        <div className="flex items-center gap-2">
          {isFederal && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => toast.success('Generating OSCAL export...', {
                description: 'OSCAL 1.1.2 SSP for federal submission will be ready in ~30s.',
              })}
            >
              <FileText className="h-3.5 w-3.5" /> Export OSCAL
            </Button>
          )}
          <Button size="sm" className="gap-1.5" onClick={onGenerate}>
            <FilePlus className="h-3.5 w-3.5" /> Generate New Report
          </Button>
        </div>
      </div>

      <div className="rounded-lg border bg-card shadow-sm overflow-x-auto">
        <div className="min-w-[1200px]">
          <div className="grid grid-cols-[minmax(300px,2fr)_90px_130px_160px_150px_110px_100px_120px_80px] gap-3 px-4 py-3 bg-muted/50 text-xs uppercase tracking-wider border-b">
            <SortableHeader label="Report Name" sortKey="name" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Format" sortKey="format" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Generated" sortKey="generatedDate" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Generated By" sortKey="generatedBy" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Period" sortKey="periodStart" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Controls" sortKey="totalControls" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Pass Rate" sortKey="passRate" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Size" sortKey="fileSizeBytes" currentSort={sortConfig} onSort={handleSort} />
            <span className="text-right text-xs text-muted-foreground">Actions</span>
          </div>

          <div className="divide-y">
            {sorted.map(report => {
              const formatStyle: Record<ReportFormat, { label: ReportFormatLabel; color: string }> = {
                pdf: { label: 'PDF', color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20' },
                excel: { label: 'Excel', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' },
                oscal: { label: 'OSCAL', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20' },
              };
              const fmt = formatStyle[report.format];

              return (
                <div
                  key={report.id}
                  className="grid grid-cols-[minmax(300px,2fr)_90px_130px_160px_150px_110px_100px_120px_80px] gap-3 px-4 py-3 items-center hover:bg-muted/20 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{report.name}</p>
                    {report.watermark && (
                      <p className="text-[10px] text-muted-foreground mt-0.5 truncate">
                        <span className="px-1 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                          {report.watermark}
                        </span>
                      </p>
                    )}
                  </div>

                  <Badge variant="outline" className={cn('text-[10px] w-fit', fmt.color)}>
                    {fmt.label}
                  </Badge>

                  <span className="text-xs text-muted-foreground">{formatDate(report.generatedDate)}</span>

                  <div className="text-xs truncate" title={report.generatedBy}>
                    {report.generatedBy}
                  </div>

                  <div className="text-xs text-muted-foreground">
                    {formatDate(report.periodStart)} – {formatDate(report.periodEnd)}
                  </div>

                  <span className="text-xs font-medium">{report.totalControls}</span>

                  <div>
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-[10px]',
                        report.passRate >= 90
                          ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20'
                          : report.passRate >= 75
                            ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
                            : 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
                      )}
                    >
                      {report.passRate}%
                    </Badge>
                  </div>

                  <span className="text-xs text-muted-foreground">
                    {formatBytes(report.fileSizeBytes)}
                  </span>

                  <div className="flex items-center justify-end gap-0.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      title="Download"
                      onClick={() => toast.success(`Downloading ${report.name}...`)}
                    >
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="More">
                          <ChevronRight className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Download as</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={() => toast.success(`Downloading ${report.name} (PDF)...`)}>
                          <FileText className="h-3.5 w-3.5 mr-2" /> PDF
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => toast.success(`Downloading ${report.name} (Excel)...`)}>
                          <FileText className="h-3.5 w-3.5 mr-2" /> Excel (.xlsx)
                        </DropdownMenuItem>
                        {isFederal && (
                          <DropdownMenuItem onSelect={() => toast.success(`Exporting ${report.name} (OSCAL)...`)}>
                            <FileText className="h-3.5 w-3.5 mr-2" /> OSCAL (.json)
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={() => toast.info('Regeneration queued')}>
                          <RefreshCw className="h-3.5 w-3.5 mr-2" /> Regenerate with latest data
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── Roadmap / POA&M Tab ─────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

function RoadmapTab({
  items, federal, controls, onChange,
}: {
  items: PoamItem[];
  federal: boolean;
  controls: Array<{ id: string; name: string }>;
  onChange: (items: PoamItem[]) => void;
}) {
  const [statusFilter, setStatusFilter] = useState<'all' | PoamItem['status']>('all');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<PoamItem | null>(null);

  const filtered = useMemo(
    () => items.filter(i => statusFilter === 'all' || i.status === statusFilter),
    [items, statusFilter],
  );

  const openCount = items.filter(i => i.status !== 'completed').length;
  const overdueCount = items.filter(i => {
    if (i.status === 'completed') return false;
    const d = daysUntil(i.targetCloseDate);
    return d !== null && d < 0;
  }).length;

  const handleAdd = () => {
    setEditingItem(null);
    setEditorOpen(true);
  };

  const handleEdit = (item: PoamItem) => {
    setEditingItem(item);
    setEditorOpen(true);
  };

  const handleSave = (payload: PoamItem) => {
    if (editingItem) {
      onChange(items.map(i => i.id === payload.id ? payload : i));
    } else {
      onChange([...items, payload]);
    }
  };

  const handleDelete = (id: string) => {
    onChange(items.filter(i => i.id !== id));
  };

  const handleClose = (item: PoamItem) => {
    onChange(items.map(i => i.id === item.id ? { ...i, status: 'completed' } : i));
    toast.success(`${item.name} marked completed`);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold">
            {federal ? 'Plan of Action & Milestones (POA&M)' : 'Improvement Plan'}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {federal
              ? '180-day maximum closure window per federal requirements. Open items must have an identified milestone path to closure.'
              : 'Controls that are not yet fully implemented with a remediation timeline.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={v => setStatusFilter(v as typeof statusFilter)}>
            <SelectTrigger className="h-9 text-xs w-40">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={handleAdd}
          >
            <Plus className="h-3.5 w-3.5" /> Add Item
          </Button>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Open Items</p>
            <p className={cn('text-2xl font-bold', openCount > 0 ? 'text-orange-600 dark:text-orange-400' : '')}>
              {openCount}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Overdue</p>
            <p className={cn('text-2xl font-bold', overdueCount > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400')}>
              {overdueCount}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Completed</p>
            <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
              {items.filter(i => i.status === 'completed').length}
            </p>
          </CardContent>
        </Card>
      </div>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center border rounded-lg bg-muted/20">
          <Flag className="h-10 w-10 text-muted-foreground/20 mb-3" />
          <p className="text-sm font-medium">No items match your filter</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(item => {
            const daysToClose = daysUntil(item.targetCloseDate);
            const overdue = daysToClose !== null && daysToClose < 0 && item.status !== 'completed';
            const dueSoon = daysToClose !== null && daysToClose >= 0 && daysToClose <= 14 && item.status !== 'completed';

            const statusStyle = {
              open: { label: 'Open', color: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20' },
              in_progress: { label: 'In Progress', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20' },
              completed: { label: 'Completed', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' },
            }[item.status];

            return (
              <Card key={item.id} className={cn(overdue && 'border-red-500/40')}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-sm font-semibold">{item.name}</h4>
                        <Badge variant="outline" className={cn('text-[10px]', statusStyle.color)}>
                          {statusStyle.label}
                        </Badge>
                        {overdue && (
                          <Badge variant="outline" className="text-[10px] bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20 gap-1">
                            <AlertTriangle className="h-2.5 w-2.5" /> Overdue
                          </Badge>
                        )}
                        {dueSoon && (
                          <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20">
                            Due Soon
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{item.description}</p>

                      <div className="flex items-center gap-4 text-[11px] text-muted-foreground flex-wrap">
                        <span className="inline-flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {item.owner}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Opened: {formatDate(item.openDate)}
                        </span>
                        <span className={cn(
                          'inline-flex items-center gap-1',
                          overdue && 'text-red-600 dark:text-red-400 font-medium',
                          dueSoon && 'text-amber-600 dark:text-amber-400 font-medium',
                        )}>
                          <Clock className="h-3 w-3" />
                          Target close: {formatDate(item.targetCloseDate)}
                          {daysToClose !== null && (
                            <span className="ml-1">
                              ({daysToClose >= 0 ? `${daysToClose}d remaining` : `${Math.abs(daysToClose)}d overdue`})
                            </span>
                          )}
                        </span>
                      </div>

                      {item.milestones.length > 0 && (
                        <div className="space-y-1 mt-2">
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Milestones</p>
                          <div className="space-y-1">
                            {item.milestones.map((ms, idx) => {
                              const isDone = ms.toLowerCase().includes('(done)');
                              const inProgress = ms.toLowerCase().includes('(in progress)');
                              const isDue = ms.toLowerCase().includes('(due)');
                              return (
                                <div key={idx} className="flex items-center gap-2 text-[11px]">
                                  <div className={cn(
                                    'h-3.5 w-3.5 rounded-full shrink-0 flex items-center justify-center border',
                                    isDone && 'bg-emerald-500 border-emerald-500',
                                    inProgress && 'bg-blue-500/40 border-blue-500',
                                    isDue && 'bg-amber-500/40 border-amber-500',
                                    !isDone && !inProgress && !isDue && 'border-muted-foreground/30',
                                  )}>
                                    {isDone && <Check className="h-2 w-2 text-white" />}
                                  </div>
                                  <span className={cn(isDone && 'line-through text-muted-foreground')}>
                                    {ms.replace(/\s*\((done|in progress|due)\)/i, '')}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 gap-1.5"
                        onClick={() => handleEdit(item)}
                      >
                        <Settings className="h-3.5 w-3.5" /> Edit
                      </Button>
                      {item.status !== 'completed' && (
                        <Button
                          size="sm"
                          className="h-8 gap-1.5"
                          onClick={() => handleClose(item)}
                        >
                          <Check className="h-3.5 w-3.5" /> Close
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <PoamEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        item={editingItem}
        federal={federal}
        controls={controls}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── Generate Report Dialog ──────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

function GenerateReportDialog({
  framework, open, onOpenChange,
}: {
  framework: Framework;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [format, setFormat] = useState<ReportFormat>('pdf');
  const [dateRange, setDateRange] = useState<'q1' | 'q2' | 'q3' | 'q4' | 'ytd' | 'last_12m' | 'custom'>('ytd');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [includeControls, setIncludeControls] = useState(true);
  const [includeEvidence, setIncludeEvidence] = useState(true);
  const [includeRisks, setIncludeRisks] = useState(true);
  const [includePolicies, setIncludePolicies] = useState(false);
  const [watermark, setWatermark] = useState('ArgusAI CONFIDENTIAL');
  const [loading, setLoading] = useState(false);

  const isFederal = framework.id === 'cmmc_l2';

  const handleGenerate = () => {
    // resolves the period, then import('@/lib/trust-downloads').downloadAuditReport(...)
    // with live controls+evidence; toasts the real filename + counts. See file.
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Generate Audit Report — {framework.name}
          </DialogTitle>
          <DialogDescription>
            Configure the report output, date range, sections, and watermark. Generation typically
            completes in 20-60 seconds depending on report size.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Format */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Report Format</Label>
            <RadioGroup
              value={format}
              onValueChange={v => setFormat(v as ReportFormat)}
              className="grid grid-cols-3 gap-2"
            >
              <Label
                htmlFor="fmt-pdf"
                className={cn(
                  'flex items-center gap-2 p-3 rounded-md border cursor-pointer transition-colors',
                  format === 'pdf' ? 'border-primary bg-primary/5' : 'hover:border-muted-foreground/40',
                )}
              >
                <RadioGroupItem value="pdf" id="fmt-pdf" />
                <div>
                  <p className="text-sm font-medium">PDF</p>
                  <p className="text-[10px] text-muted-foreground">Audit-ready</p>
                </div>
              </Label>
              <Label
                htmlFor="fmt-excel"
                className={cn(
                  'flex items-center gap-2 p-3 rounded-md border cursor-pointer transition-colors',
                  format === 'excel' ? 'border-primary bg-primary/5' : 'hover:border-muted-foreground/40',
                )}
              >
                <RadioGroupItem value="excel" id="fmt-excel" />
                <div>
                  <p className="text-sm font-medium">Excel</p>
                  <p className="text-[10px] text-muted-foreground">Evidence matrix</p>
                </div>
              </Label>
              <Label
                htmlFor="fmt-oscal"
                className={cn(
                  'flex items-center gap-2 p-3 rounded-md border cursor-pointer transition-colors',
                  format === 'oscal' ? 'border-primary bg-primary/5' : 'hover:border-muted-foreground/40',
                  !isFederal && 'opacity-50',
                )}
              >
                <RadioGroupItem value="oscal" id="fmt-oscal" disabled={!isFederal} />
                <div>
                  <p className="text-sm font-medium">OSCAL</p>
                  <p className="text-[10px] text-muted-foreground">
                    {isFederal ? 'Federal SSP' : 'Federal only'}
                  </p>
                </div>
              </Label>
            </RadioGroup>
          </div>

          {/* Date Range */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Reporting Period</Label>
            <Select value={dateRange} onValueChange={v => setDateRange(v as typeof dateRange)}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="q1">Q1 2026 (Jan 1 – Mar 31)</SelectItem>
                <SelectItem value="q2">Q2 2026 (Apr 1 – Jun 30)</SelectItem>
                <SelectItem value="q3">Q3 2026 (Jul 1 – Sep 30)</SelectItem>
                <SelectItem value="q4">Q4 2026 (Oct 1 – Dec 31)</SelectItem>
                <SelectItem value="ytd">Year to date</SelectItem>
                <SelectItem value="last_12m">Last 12 months</SelectItem>
                <SelectItem value="custom">Custom range…</SelectItem>
              </SelectContent>
            </Select>

            {dateRange === 'custom' && (
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                  <Label className="text-[11px] text-muted-foreground">Start</Label>
                  <Input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="h-9" />
                </div>
                <div>
                  <Label className="text-[11px] text-muted-foreground">End</Label>
                  <Input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="h-9" />
                </div>
              </div>
            )}
          </div>

          {/* Include Sections */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Include Sections</Label>
            <div className="grid grid-cols-2 gap-2">
              <Label className="flex items-center gap-2 p-2.5 rounded-md border cursor-pointer hover:bg-muted/30">
                <Checkbox checked={includeControls} onCheckedChange={v => setIncludeControls(!!v)} />
                <span className="text-sm">Controls</span>
              </Label>
              <Label className="flex items-center gap-2 p-2.5 rounded-md border cursor-pointer hover:bg-muted/30">
                <Checkbox checked={includeEvidence} onCheckedChange={v => setIncludeEvidence(!!v)} />
                <span className="text-sm">Evidence</span>
              </Label>
              <Label className="flex items-center gap-2 p-2.5 rounded-md border cursor-pointer hover:bg-muted/30">
                <Checkbox checked={includeRisks} onCheckedChange={v => setIncludeRisks(!!v)} />
                <span className="text-sm">Risks</span>
              </Label>
              <Label className="flex items-center gap-2 p-2.5 rounded-md border cursor-pointer hover:bg-muted/30">
                <Checkbox checked={includePolicies} onCheckedChange={v => setIncludePolicies(!!v)} />
                <span className="text-sm">Policies</span>
              </Label>
            </div>
          </div>

          {/* Watermark */}
          <div className="space-y-2">
            <Label htmlFor="watermark" className="text-sm font-medium">Watermark Text</Label>
            <Input
              id="watermark"
              value={watermark}
              onChange={e => setWatermark(e.target.value)}
              placeholder="ArgusAI CONFIDENTIAL"
              className="h-9"
            />
            <p className="text-[11px] text-muted-foreground">
              Appears diagonally across every page. Leave blank for no watermark.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleGenerate}
            disabled={loading || (!includeControls && !includeEvidence && !includeRisks && !includePolicies)}
            className="gap-1.5"
          >
            {loading ? (
              <>
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <Download className="h-3.5 w-3.5" />
                Generate & Download
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
